import { ChangeDetectorRef, Component, ElementRef, HostListener, QueryList, ViewChild, computed, contentChildren, effect, inject, input, model, output, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { assert, assertTrue } from 'src/app/utils/assert.util';
import { readBreakpoint } from 'src/app/utils/breakpoint.util';
import { OverlayControl } from 'src/app/interfaces/overlay-control';
import { Navigation2Service } from '../../services/navigation2.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { config, expand, map, max, Observable, skip } from 'rxjs';
import * as THREE from 'three';
import { GLTFLoader, TrackballControls, CSS2DObject, CSS2DRenderer, ThreeMFLoader, OrbitControls, HorizontalBlurShader, VerticalBlurShader, EXRLoader, RGBELoader, DRACOLoader } from 'three-stdlib';
import { getAssetURL } from 'src/app/constants/assets';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { ItemAssetsService } from 'src/app/services/item-assets/item-assets.service';
import { ItemAssetLocationToThreeJSKeyMap } from 'src/app/utils/item-asset.util';
import { ItemAsset } from 'src/app/interfaces/item-asset';
import { CheckoutService } from '../../services/checkout.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { BookmarksService } from '../../services/bookmarks.service';
import { TextureFeature } from 'src/app/interfaces/texture-feature';
import { SettingType } from 'src/app/types/setting-type';
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast, computeBatchedBoundsTree, disposeBatchedBoundsTree } from 'three-mesh-bvh';
import { DiamondShaderMaterial } from './models/DiamondShaderMaterial';
import { StoneArrangementsService } from 'src/app/services/stone-arrangements/stone-arrangements.service';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneSettingsService } from 'src/app/services/stone-settings/stone-settings.service';
import { StoneShapesService } from 'src/app/services/stone-shapes/stone-shapes.service';
import { StoneSizesService } from 'src/app/services/stone-sizes/stone-sizes.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneTypesService } from 'src/app/services/stone-types/stone-types.service';
import { MatButton } from '@angular/material/button';
import { StoneCountsProviderService } from 'src/app/providers/stone-counts/stone-counts.provider.service';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { MainImageService } from 'src/app/services/main-image/main-image.service';
import { ThisReceiver } from '@angular/compiler';
import { engravingSpecialChars } from 'src/app/utils/engraving.util';
import { DispatcherService } from 'src/app/services/dispatcher/dispatcher.service';
import { CachedItemAsset } from 'src/app/interfaces/cached-item-asset';
import HeightToNormal, { NORMA_MAP_TYPES } from 'height-to-normal-map';
import { EngravingTypesService } from 'src/app/services/engraving-types/engraving-types.service';
import { CustomerThemeService } from 'src/app/services/customer-theme/customer-theme.service';

@Component({
  selector: 'app-main-image',
  templateUrl: './main-image.component.html',
  styleUrls: ['./main-image.component.scss']
})
export class MainImageComponent {
  private engravingTypesService = inject(EngravingTypesService);
  private customerThemeService = inject(CustomerThemeService);
  logo = computed(() => this.customerThemeService.theme()?.logo ?? null);
  mobileLogo = computed(() => this.customerThemeService.theme()?.mobileLogo ?? this.logo());
  private dispatcherService = inject(DispatcherService);
  private mainImageService = inject(MainImageService);
  private checkoutService = inject(CheckoutService);
  private rulesService = inject(RulesService);
  private sizesService = inject(SizesService);
  private stoneCountsProvider = inject(StoneCountsProviderService);
  private stoneTypesWithShapesService = inject(StoneTypesWithShapesService);
  private stoneSizesService = inject(StoneSizesService);
  private stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private stoneArrangementsService = inject(StoneArrangementsService);
  private stoneShapesService = inject(StoneShapesService);
  private stoneTypesService = inject(StoneTypesService);
  private stoneSettingsService = inject(StoneSettingsService);
  private configuredItemService = inject(ConfiguredItemService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private navigationService = inject(Navigation2Service);
  private loadingService = inject(LoadingService);
  private itemAssetsService = inject(ItemAssetsService);
  private bookmarksService = inject(BookmarksService);
  modelCreated = output<boolean>();
  canViewStyleImages = input<boolean>(true);
  @ViewChild('imageRef', { static: true }) imgRef!: ElementRef<HTMLImageElement>;
  removeButtons = viewChildren<QueryList<ElementRef<HTMLButtonElement>>, ElementRef>('removeButton', { read: ElementRef });
  viewStyleImagesButton = viewChild<MatButton>('viewStyleImagesButton');
  printImg = viewChild<ElementRef<HTMLImageElement>>('printImg');
  controlOverlay = viewChild<ElementRef<HTMLDivElement>>('controlOverlay');
  imgCanvas = viewChild<ElementRef<HTMLCanvasElement>>('imgCanvas');
  overlayButtons = viewChildren<QueryList<ElementRef<HTMLButtonElement>>, ElementRef>('overlayButton', { read: ElementRef });
  positionIndicator = viewChild<ElementRef<HTMLDivElement>>('positionIndicator');
  changeHoverExplanations = viewChildren<QueryList<ElementRef<HTMLButtonElement>>, ElementRef>('changeHoverExplanation', { read: ElementRef });
  viewStyleImagesClicked = output();
  isSharing = input(false);
  isComparing = input(false);
  skusToCompare = computed(() => this.bookmarksService.skusToCompare());
  configuredItemObservable?: Observable<ConfiguredItem>;
  isOverlayRendered = false;
  controlToAnimateIn: OverlayControl | null = null;
  controlToAnimateOut: OverlayControl | null= null;
  windowWidth = signal<number>(window.innerWidth);
  windowHeight = signal<number>(window.innerHeight);
  // cardMode = computed(() => {
  //   if (this.windowWidth() > 770) {
  //     return 'Normal';
  //   }

  //   return 'None';
  // });
  /**
   * Two folds, on opposite axes, and they are mutually exclusive by construction:
   *   FoldedHorizontal -- limited WIDTH: cards get narrower and taller (the original fold)
   *   FoldedVertical   -- limited HEIGHT: cards get wider and shorter
   * The height check partitions the space, so 'both at once' cannot arise. See updateCardMode.
   */
  cardMode = signal<'None' | 'Normal' | 'FoldedVertical' | 'FoldedHorizontal' | 'Stacked'>('Normal');

  isMouseDown = false;
  shouldCancelMouseClick = false;
  isRingHovered = signal<boolean>(false);
  controlLabelToStyleHovered = signal<string | null>(null);
  didInteractWithImage = signal<boolean>(false);
  shouldClearHover = false;
  /**
   * Annotations are hidden BELOW this width. Strictly less-than, to match the CSS
   * `min-width: $screen-sm` (770px) that switches the app to its desktop layout -- with <= the two
   * disagreed at exactly 770px: annotations hidden while the layout was still desktop (ADO #8909).
   */
  noAnnotationLowerBound = readBreakpoint('--breakpoint-screen-sm', 770);
  imageZoom = 1;
  imageX = 0;
  imageY = 0;
  dragX = 0;
  dragY = 0;
  ringImage?: any
  scene?: THREE.Scene;
  camera?: THREE.PerspectiveCamera;
  snapshotRenderer?: THREE.WebGLRenderer;
  renderer?: THREE.WebGLRenderer;
  cssRenderer?: CSS2DRenderer;
  compareCSSRenderer?: CSS2DRenderer;
  trackballControls?: TrackballControls;
  orbitControls?: OrbitControls;
  shouldShowAnnotations = signal(this.windowWidth() >= this.noAnnotationLowerBound);
  model: any;
  raycaster: THREE.Raycaster = new THREE.Raycaster();
  pointer?: THREE.Vector2;
  positionIndicatorBoundingSphere?: THREE.Sphere;
  positionIndicatorObject?: CSS2DObject;
  shouldCancelSectionAnimation = false;
  shouldCancelCameraAnimation = false;
  didUserPan = false;
  angles: Record<string, {azimuthal: number, polar: number, zPosition: number}> = {
    'Width': {
      polar: 0,
      azimuthal: -1.26,
      zPosition: 0.12
    },
    'Design': {
      polar: 0.57,
      azimuthal: -0.41,
      zPosition: 1.95
    },
    'Center Material': {
      polar: 0.21,
      azimuthal: -0.43,
      zPosition: 0.31
    },
    'Edge Material': {
      polar: 1.16,
      azimuthal: 0.71,
      zPosition: 1.70
    },
    'Profile': {
      polar: 1.03,
      azimuthal: -1.32,
      zPosition: 3.59
    },
    'Finger Size': {
      polar: 1.05,
      azimuthal: 0,
      zPosition: 2.68
    },
    'Gemstones': {
      polar: 0,
      azimuthal: -0.012152701474889793,
      zPosition: 2.68
    }
  };
  selectedAttributes = computed(() => this.selectedAttributeService.getValue());
  actualSelectedAttributes = computed(() => Object.keys(this.selectedAttributes()).reduce((obj, key) => {
    if (key === 'primaryMaterial') {
      obj['Material'] = [this.selectedAttributes().primaryMaterial, this.selectedAttributes().secondaryMaterial].filter(m => m).join(', ');
      return obj;
    }
    if (['engravingType','engravingText', 'secondaryMaterial'].includes(key)) {
      return obj;
    }
    const keyArr = key.split('');
    keyArr[0] = keyArr[0].toUpperCase();
    const newKey = keyArr.join('');
    obj[newKey] = this.selectedAttributes()[key as keyof(ConfiguredItem)];
    return obj;
  }, {} as any));
  selectedAttributeCount = computed(() => Object.keys(this.actualSelectedAttributes()).filter(k => this.actualSelectedAttributes()[k]).length);
  stoneCount = signal<number | null>(null);
  systemChanges = computed(() => this.selectedAttributeService.systemChanges());
  overlayWidthBasis = 1419;
  overlayHeightBasis = 1031;
  overlayGroup?: THREE.Group;
  orbitControlsChangeHandler?: Function;
  floorPlane?: THREE.Mesh;
  doesModelNeedUpdate: boolean = false;
  prevItem?: ConfiguredItem;
  modelGroup?: THREE.Group;
  renderTarget?: THREE.WebGLRenderTarget;
  blurRenderTarget?: THREE.WebGLRenderTarget;
  depthMaterial?: THREE.MeshDepthMaterial;
  shadowCamera?: THREE.OrthographicCamera;
  blurPlane?: THREE.Mesh;
  horizontalBlurMaterial?: THREE.ShaderMaterial;
  verticalBlurMaterial?: THREE.ShaderMaterial;
  shouldDoInitialCameraReposition = true;
  farE: number | null = null;
  singleViewControlPositionOverride = { x: 610, y: 120 };
  currentItemAssets: ItemAsset[] = [];
  controls: OverlayControl[] = [
    {
      displayName: 'Design',
      key: 'design',
      point: { x: 1094, y: 444 },
      label: 90 + 70,
      calculateOriginOffset: (overlayButton: HTMLElement) => ({x: overlayButton.getBoundingClientRect().width, y: overlayButton.getBoundingClientRect().height}),
      isActive: true
    },
    {
      displayName: 'Profile',
      key: 'form',
      point: { x: 279, y: 674 },
      label: 90 + 15,
      calculateOriginOffset: (overlayButton: HTMLElement) => ({x: overlayButton.getBoundingClientRect().width, y: overlayButton.getBoundingClientRect().height}),
      isActive: false
    },
    {
      displayName: 'Edge Material',
      key: 'primaryMaterial',
      point: { x: 1050, y: 890 },
      label: 90 - 15,
      calculateOriginOffset: (overlayButton: HTMLElement) => ({x: 0, y: overlayButton.getBoundingClientRect().height}),
      isActive: false
    },
    {
      displayName: 'Center Material',
      key: 'secondaryMaterial',
      point: { x: 279, y: 674 },
      label: 90 - 70,
      calculateOriginOffset: (overlayButton: HTMLElement) => ({x: 0, y: overlayButton.getBoundingClientRect().height}),
      isActive: false
    },
    {
      displayName: 'Width',
      key: 'width',
      point: { x: 1470, y: 554 },
      label: 270 + 70,
      calculateOriginOffset: (overlayButton: HTMLElement) => ({x: 0, y: 0}),
      isActive: false
    },
    // {
    //   displayName: 'Engraving',
    //   key: 'engravingText',
    //   point: { x: 536, y: 1055 },
    //   label: { x: 356, y: 1200 },
    //   isActive: false
    // },
    {
      displayName: 'Finger Size',
      key: 'size',
      point: { x: 783, y: 801 },
      label: 270 + 15,
      calculateOriginOffset: (overlayButton: HTMLElement) => ({x: 0, y: 0}),
      isActive: false
    },
    {
      displayName: 'Gemstones',
      key: 'gemstones',
      point: { x: 674, y: 640 },
      label: 270 - 15,
      calculateOriginOffset: (overlayButton: HTMLElement) => ({x: overlayButton.getBoundingClientRect().width, y: 0}),
      isActive: false
    },
    {
      displayName: 'Engraving',
      key: 'engraving',
      point: { x: 674, y: 640 },
      label: 270 - 70,
      calculateOriginOffset: (overlayButton: HTMLElement) => ({x: overlayButton.getBoundingClientRect().width, y: 0}),
      isActive: false
    },
  ];
  adjustedControls: OverlayControl[] = [];
  shouldDoInitialSetup = true;
  dracoLoader: DRACOLoader;
  gltfLoader: GLTFLoader;
  previousItemAssets: ItemAsset[] = [];
  normalConverter?: any
  baseTextCanvas = document.createElement('canvas');
  mapCanvas = document.createElement('canvas');
  tmpCanvas = document.createElement('canvas');
  normalCanvas = document.createElement('canvas');
  pbrCanvas = document.createElement('canvas');
  normalImage = document.createElement('img');
  insideTextureCache = new Map<string, CachedItemAsset>();
  // C1/C2 (b): handle on the in-flight inside-texture prefetch kicked off by
  // loadModel. createModel joins it before the engraving step (the only reader of
  // insideTextureCache). Null outside loadModel (e.g. compare-models path).
  // Private field, not a signature change.
  pendingInsideTextureFill: Promise<void> | null = null;

  constructor() {
    (window as any).CSS2DObject = CSS2DObject;
    (window as any).mainImageComponent = this;
    (window as any).three = THREE;
    (window as any).EXRLoader = EXRLoader;
    (window as any).RGBELoader = RGBELoader;

    this.mainImageService.getSnapshot = this.getCurrentRingSnapshot.bind(this, 'image/jpeg');

    THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
    THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
    THREE.Mesh.prototype.raycast = acceleratedRaycast;

    THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
    THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
    THREE.Mesh.prototype.raycast = acceleratedRaycast;

    THREE.BatchedMesh.prototype.computeBoundsTree = computeBatchedBoundsTree;
    THREE.BatchedMesh.prototype.disposeBoundsTree = disposeBatchedBoundsTree;
    THREE.BatchedMesh.prototype.raycast = acceleratedRaycast;

    this.dracoLoader = new DRACOLoader();
    this.gltfLoader = new GLTFLoader();

    this.dracoLoader.setDecoderPath('/assets/draco/');
    this.dracoLoader.preload();

    this.gltfLoader.setDRACOLoader(this.dracoLoader);

    this.configuredItemObservable = toObservable(this.selectedAttributeService.configuredItem)

    // effect(() => {
    //   const stonesViewIndex = untracked(() => this.navigationService.views()).findIndex(v => v.title === 'Gemstones');
    //   if (stonesViewIndex) {
    //     this.navigationService.setViewdoesNextPrevSkip(stonesViewIndex, this.windowWidth() >= 770 && !this.selectedAttributes().gemstoneType);
    //     if (untracked(() => this.navigationService.activeViewIndex()) === stonesViewIndex && this.windowWidth() >= 770 && !this.selectedAttributes().gemstoneType) {
    //       this.navigationService.navigateTo(0);
    //     }
    //   }
    // }, { allowSignalWrites: true });

    effect(async () => {
      if (this.selectedAttributes().gemstoneType) {
        this.loadingService.load('MainImage::DisplayedValue::StoneCounts');
        const stoneCount = await this.stoneCountsProvider.get(this.selectedAttributes());
        this.stoneCount.set(stoneCount);
        this.loadingService.done('MainImage::DisplayedValue::StoneCounts');
      } else {
        this.stoneCount.set(0);
      }
    }, { allowSignalWrites: true });

    effect(() => {
      this.shouldShowAnnotations.set(window.innerWidth >= this.noAnnotationLowerBound && !this.isComparing());
      if (!this.isComparing() && this.camera) {
        this.cleanUpCompareGroup();
        this.setupNormalCamera();
      }
      if (this.modelGroup && this.scene) {
        this.scene.visible = !this.isComparing();
        this.modelGroup.visible = !this.isComparing();
        (this.scene.children.find(c => c.name === 'Gemstones') ?? {} as any).visible = !this.isComparing();
      }
    }, { allowSignalWrites: true });

    effect(() => {
      this.changeHoverExplanations().forEach(explanation => {
        explanation.nativeElement.setAttribute('style', `--container-position: ${(explanation.nativeElement.getBoundingClientRect().height / 2 - explanation.nativeElement.parentElement.getBoundingClientRect().height / 2) * -1}px`);
      });
    });

    effect(() => {
      const windowWidth = this.windowWidth();
      if (this.orbitControls && this.farE) {
        this.setOrbitControlsZoomOptions(windowWidth);
      }
    });

    effect(async () => {
      const imgCanvasElement = this.imgCanvas()?.nativeElement;
      const configuredItem = this.selectedAttributeService.configuredItem();
      const overlayButtons = this.overlayButtons();

      await this.dispatcherService.getQueue('main').run(async () => {
        await this.loadModel(imgCanvasElement, configuredItem, overlayButtons);
      });
      // this.loadModelPromises.push(promise);
    }, { allowSignalWrites: true });

    // Re-apply the camera zoom whenever the card mode changes. setOrbitControlsZoomOptions is
    // otherwise only called from setupNormalCamera (during model load, when cardMode is still its
    // initial 'Normal') and from an effect on windowWidth. So a page LOADED straight into Stacked
    // never got the stacked zoom at all -- it only appeared if you resized into it, which made the
    // ring look smaller after a refresh than it had a moment earlier (ADO #8909).
    effect(() => {
      this.cardMode();
      if (this.farE && this.orbitControls) {
        this.setOrbitControlsZoomOptions(window.innerWidth);
      }
    });

    effect(() => {
      const shouldShowAnnotations = this.shouldShowAnnotations();
      const controlOverlay = this.controlOverlay();

      if (window.innerWidth >= this.noAnnotationLowerBound) {
        this.triggerGAEvent('click', 'toggle_ring_labels', `${shouldShowAnnotations}`);
      }

      if (this.overlayGroup && controlOverlay) {
        if (!shouldShowAnnotations) {
          controlOverlay.nativeElement.style.display = 'none';
          this.overlayGroup.removeFromParent();
        } else {
          // Clear the inline value rather than hardcoding 'block'. An inline declaration beats the
          // stylesheet, so 'block' silently overrode `display: flex` on .OverlayControls.Stacked --
          // a block container ignores `gap`, which is why the stacked list lost its spacing
          // (ADO #8909). Removing it lets CSS decide: flex when stacked, block otherwise.
          controlOverlay.nativeElement.style.display = '';
          this.scene?.add(this.overlayGroup);
        }
      }
    });

    effect(() => {
      const centerControl = this.controls.find(c => c.displayName === 'Center Material');
      (centerControl ?? {} as any).isHidden = this.navigationService.views().find(v => v.title === 'Center Material')?.isHidden;
    });

    effect(() => {
      const activeView = this.navigationService.activeView();
      const printImg = this.printImg()?.nativeElement;
      if (activeView && activeView.title === 'Retailer Summary' && printImg) {
        this.setPrintImage();
      }
    })

    effect(() => {
      const viewIndex = this.navigationService.activeViewIndexAlwaysriggers();
      this.controls.forEach((c,i) => {
        if (c.isActive && viewIndex !== i) {
          this.controlToAnimateOut = c;
        } else if (!c.isActive && viewIndex === i) {
          this.controlToAnimateIn = c;
          c.isActive = true;

        }

        c.isActive = viewIndex === i;
      });

      this.positionCameraForActiveView();
    });

    effect(async () => {
      const skusToCompare = this.skusToCompare();
      const imgCanvasElement = this.imgCanvas()?.nativeElement;
      const stoneGroups: ({ sort: number, group: any | null })[] = [];

      if (untracked(() => this.isComparing()) && this.scene && imgCanvasElement) {
        (this.modelGroup ?? {} as any).visible = false;
        (this.scene.children.find(c => c.name === 'Gemstones') ?? {} as any).visible = false;
        this.scene.visible = false;
        this.cleanUpCompareGroup();

        if (!this.compareCSSRenderer) {
          this.compareCSSRenderer = new CSS2DRenderer();
          this.compareCSSRenderer.setSize(imgCanvasElement.clientWidth, imgCanvasElement.clientHeight);
          this.compareCSSRenderer.domElement.style.position = 'absolute';
          this.compareCSSRenderer.domElement.style.top = '0px';
          this.compareCSSRenderer.domElement.style.height = '0px';
          this.compareCSSRenderer.domElement.style.width = '0px';
          this.compareCSSRenderer.domElement.style.overflow = 'unset';
          if (this.windowWidth() <= this.noAnnotationLowerBound) {
            this.compareCSSRenderer.domElement.style.transform = '';
          } else {
            this.compareCSSRenderer.domElement.style.transform = 'translateY(5px)';
          }
          imgCanvasElement.parentElement?.appendChild(this.compareCSSRenderer.domElement);
        }

        if (skusToCompare.length > 0) {
          this.loadingService.load('mainimage::comparemodels');
          const compareGroup = new THREE.Group();
          compareGroup.name = 'CompareGroup';
          compareGroup.rotation.set(0,1,0);
          let lastYPosition = 0;
          const compareModels = await Promise.all(skusToCompare.map(async (compareSKU, i) => {
            const configuredItem = this.configuredItemService.getAttributesFromNewSKU(compareSKU);
            const itemAssets = await this.itemAssetsService.get(configuredItem);
            const compareModel = await this.createModel(configuredItem, itemAssets);
            compareModel.scene.rotation.set(0, 15 * Math.PI / 180, 0);

            const compareModelGroup = new THREE.Group();
            compareModelGroup.name = i.toString();
            compareModelGroup.add(compareModel.scene);

            const nativeLabel = document.createElement('div');
            nativeLabel.classList.add('CompareRingLabel', 'Flexbox', 'Align--center', 'Justify--center');
            nativeLabel.onclick = () => this.bookmarksService.select(i);
            nativeLabel.innerHTML = `<span>${String.fromCharCode(65 + i)}</span>`;
            const label = new CSS2DObject(nativeLabel);
            label.center.set(0.5, 0);
            const boundingBox = new THREE.Box3().setFromObject(compareModel.scene, true);
            label.position.set((boundingBox.max.z) * -1.3, 0, 0);
            compareModelGroup.add(label);

            const modelMeshes: any[] = [];
            compareModel.scene.traverse((descendant: any) => {
              if (descendant.isMesh) {
                modelMeshes.push(descendant);
              }
            });

            if (configuredItem.gemstoneType) {
              const itemAssetsForGemstones = itemAssets.filter(itemAsset => itemAsset.location === 'Outside') ?? [];
              const width = Math.round((new THREE.Box3().setFromObject(modelMeshes[0], true).getSize(new THREE.Vector3).y + Number.EPSILON) * 10 / 5) / 10 * 5;
              await this.generateSettingTextures2(modelMeshes[0].material, configuredItem, width, itemAssetsForGemstones);
              const group = await this.addStones(configuredItem, compareModel, compareGroup);
              group.name = `${group.name}-${i}`;
              stoneGroups.push({ sort: i, group: group });
            } else {
              stoneGroups.push({ sort: i, group: null });
            }

            return compareModelGroup;
          }));

          stoneGroups.sort((a,b) => a.sort - b.sort);
          compareModels.sort((a,b) => +a.name - +b.name).forEach((compareModelGroup, i) => {
            const boundingBox = new THREE.Box3().setFromObject(compareModelGroup, true);
            compareModelGroup.position.y += lastYPosition;
            if (stoneGroups[i].group) {
              stoneGroups[i].group.position.y += lastYPosition;
            }
            lastYPosition +=  5 + boundingBox.max.y;
            compareGroup.add(compareModelGroup)
          });
          this.scene.add(compareGroup);

          const boundingBox = new THREE.Box3();
          boundingBox.setFromObject(compareGroup, true);

          assert(this.camera, 'No camera');
          const fov = this.camera.fov * Math.PI / 180;
          const size = new THREE.Vector3();
          boundingBox.getSize(size);
          const maxDim = Math.max(size.x, size.y, size.z);
          const z = (Math.abs(maxDim / 4 * Math.tan(fov * 2))) * 0.6;

          const minZ = boundingBox.min.z;
          const farE = (minZ < 0) ? -minZ + z : z - minZ;

          assert(this.orbitControls, 'No orbit controls');

          this.camera.far = maxDim * 5;
          this.camera.updateProjectionMatrix();

          const sceneCenter = boundingBox.getCenter(new THREE.Vector3());
          this.camera.position.set(farE * 2, farE * 2, farE * 2);
          this.orbitControls.target.set(sceneCenter.x, sceneCenter.y, sceneCenter.z);
          this.camera.lookAt(sceneCenter.x, sceneCenter.y, sceneCenter.z);

          this.orbitControls.maxDistance = this.windowWidth() >= this.noAnnotationLowerBound ? farE * 2 : farE * 1.2;
          this.orbitControls.update();
          this.orbitControls.setAzimuthalAngle(90 * Math.PI / 180)
          this.orbitControls.minDistance = this.orbitControls.maxDistance;
          this.orbitControls.update();

          this.scene.visible = true;
          this.loadingService.done('mainimage::comparemodels');
        }
      }
    }, { allowSignalWrites: true });
  }

  setPrintImage() {
    const printImg = this.printImg()?.nativeElement;
    assert(printImg, 'No printImg');
    this.loadingService.load('mainimage::printimg')
    this.imgCanvas()?.nativeElement.toBlob((blob) => {
      if (blob) {
        printImg.src = URL.createObjectURL(blob);
        this.loadingService.done('mainimage::printimg')
      }
    });
  }

  async loadModelForAdminControls(gltfFileOverride: string | undefined = undefined) {
    const imgCanvasElement = this.imgCanvas()?.nativeElement;
    const configuredItem = this.selectedAttributeService.configuredItem();
    const overlayButtons = this.overlayButtons();
    return this.loadModel(imgCanvasElement, configuredItem, overlayButtons, gltfFileOverride);
  }

  async loadModel(imgCanvasElement: HTMLCanvasElement | undefined, configuredItem: ConfiguredItem, overlayButtons: readonly ElementRef<any>[], gltfFileOverride: string | undefined = undefined) {
    this.loadingService.load('main-image:viewer');
    let didModelSucceeed = true;

    const prevItem = this.prevItem ?? {
      design: null,
      form: null,
      primaryMaterial: null,
      secondaryMaterial: null,
      width: null,
      engravingType: null,
      engravingText: null,
      size: null,
      gemstoneType: null,
      gemstoneArrangement: null,
      gemstoneSize: null,
      gemstoneSetting: null,
      gemstoneShape: null
    };

    const changes = (Object.keys(configuredItem) as (keyof ConfiguredItem)[]).reduce((changes, key) => {
      if (configuredItem[key] !== prevItem[key]) {
        changes.push(key);
      }
      return changes;
    }, [] as string[]);

    const itemAssets = (gltfFileOverride || (changes.some(k => !k.includes('size')) && changes.some(k => !k.includes('engraving')))) ? await this.itemAssetsService.get(configuredItem) : null;
    // C1/C2 (b): start the inside-texture prefetch WITHOUT awaiting it here, so its
    // fetches overlap addTextures (inside createModel / further below) on the wire.
    // Per-texture logic is unchanged; the promise is awaited before any code path
    // that reads insideTextureCache (engraving textures).
    const pendingInsideTextureCache = itemAssets ? this.cacheInsideTextures(itemAssets) : null;
    // Join point for createModel's engraving step; see field comment. Set before
    // any await of the cache or createModel promises so createModel always sees it.
    this.pendingInsideTextureFill = pendingInsideTextureCache;
    const modelAsset = itemAssets?.find(ia => ia.type === 'Model');
    const previousModelAsset = this.previousItemAssets.find(ia => ia.type === 'Model');

    // Hoisted (pure reorder): both predicates depend only on gltfFileOverride,
    // changes and the model-asset filenames, so computing them before the
    // setup/dispose awaits changes nothing but ordering. Needed to kick off the
    // .glb request before awaiting doInitialSetup (C1/C2 (a)).
    const shouldUpdateRender = gltfFileOverride || changes.some(k => !k.includes('engraving') && !k.includes('size'));
    const didCreateModel = gltfFileOverride || ((modelAsset && modelAsset?.fileName !== previousModelAsset?.fileName) || changes.some(k => ['width', 'form'].includes(k)));

    if (imgCanvasElement && overlayButtons) {
      // C1/C2 (a): .glb-first. createModel calls gltfLoader.load synchronously, so
      // starting it here puts the .glb request on the wire BEFORE awaiting
      // doInitialSetup/.exr below. doInitialSetup still runs; only the ordering of
      // the two loads changes. createModel touches only its own locals (renderer
      // reads default safely pre-setup), never this.model/this.scene, so it cannot
      // collide with the dispose path or the setup below.
      const modelPromise = (itemAssets && shouldUpdateRender && didCreateModel) ? this.createModel(configuredItem, itemAssets, gltfFileOverride) : null;
      if (!this.shouldDoInitialSetup && !configuredItem.gemstoneArrangement) {
        this.removeStones(this.model);
      }

      if (this.shouldDoInitialSetup) {
        await this.doInitialSetup(imgCanvasElement);
        this.shouldDoInitialSetup = false;
      } else if (gltfFileOverride || ((modelAsset && modelAsset?.fileName !== previousModelAsset?.fileName) || changes.some(k => ['width', 'form'].includes(k)))) {
        this?.scene?.children?.find(c => c.name === 'model group')?.traverse((descendant: any) => {
          if (descendant.isMesh) {
            Object.keys(descendant.material).forEach(k => {
              if (!!descendant.material[k]?.dispose && !k.startsWith('_')) {
                descendant.material[k].dispose();
              }
            });

            descendant.material?.dispose();
          }
        });

        this.renderer?.setAnimationLoop(null);
        this.model?.scene.removeFromParent();
        this.overlayGroup?.removeFromParent();
        this.floorPlane?.removeFromParent();
        this.modelGroup?.removeFromParent();
        this.blurPlane?.removeFromParent();
        this.horizontalBlurMaterial?.dispose()
        this.renderTarget?.dispose();
        this.blurRenderTarget?.dispose();
        this.depthMaterial?.dispose();
        this.verticalBlurMaterial?.dispose();
        this.shadowCamera?.removeFromParent();
        this.positionIndicatorObject?.removeFromParent();
        // (this.orbitControls as any)?.removeEventListener('change', this.orbitControlsChangeHandler);
        delete this.model;
        delete this.overlayGroup;
        delete this.floorPlane;
        delete this.modelGroup;
        delete this.blurPlane;
        delete this.horizontalBlurMaterial;
        delete this.renderTarget;
        delete this.blurRenderTarget;
        delete this.depthMaterial;
        delete this.verticalBlurMaterial;
        delete this.shadowCamera;
        delete this.positionIndicatorObject;
      }

      // (hoisted above: shouldUpdateRender / didCreateModel computed pre-setup)
      // if (gltfFileOverride || changes.some(k => !k.includes('engraving') && !k.includes('size'))) {
      if (shouldUpdateRender) {
        assert(itemAssets, 'No item assets provided');
        // if (gltfFileOverride || ((modelAsset && modelAsset?.fileName !== previousModelAsset?.fileName) || changes.some(k => ['width', 'form'].includes(k)))) {
        if (didCreateModel) {
          assert(this.renderer, 'Renderer was null');

          try {
            // C1/C2 (a): await the .glb-first promise kicked off above instead of
            // calling createModel here. Non-null exactly when this branch is taken
            // (same itemAssets/shouldUpdateRender/didCreateModel triple).
            assert(modelPromise, 'Model promise was null');
            this.model = await modelPromise;

            this.modelGroup = new THREE.Group();
            this.modelGroup.name = 'model group'
            this.modelGroup.add(this.model.scene);

            this.renderer.setClearColor( 0xffffff, 0);

            this.model.scene.updateWorldMatrix(true, true);

            const boundingBoxes: THREE.Box3[] = [];
            this.model.scene.traverse((child: any) => {
              if (child.isMesh) {
                const boundingBox = new THREE.Box3();
                boundingBox.setFromObject(child);
                boundingBoxes.push(boundingBox);
              }
            });

            const sizes = boundingBoxes.map((boundingBox) => {
              const size = new THREE.Vector3()
              boundingBox.getSize(size);
              return size;
            });

            const dim = sizes.reduce((max, size) => {
              return Math.max(max, size.x, size.y, size.z);
            }, 0);

            const originalModelSizeVector = new THREE.Vector3(dim, dim, dim);
            const modelSizeVector = new THREE.Vector3(originalModelSizeVector.x, originalModelSizeVector.y, originalModelSizeVector.z);
            modelSizeVector.multiplyScalar(2);
            modelSizeVector.x *= 1.2;

            this.renderTarget = new THREE.WebGLRenderTarget(512, 512);
            this.renderTarget.texture.generateMipmaps = false;

            this.blurRenderTarget = new THREE.WebGLRenderTarget(512, 512);
            this.blurRenderTarget.texture.generateMipmaps = false;

            const shadowsPlaneGeo = new THREE.PlaneGeometry(modelSizeVector.x, modelSizeVector.y).rotateX(Math.PI / 2);
            this.blurPlane = new THREE.Mesh(shadowsPlaneGeo);
            this.blurPlane.name = 'Blur Plane'

            this.shadowCamera = new THREE.OrthographicCamera(-modelSizeVector.x / 2, modelSizeVector.x / 2, modelSizeVector.y / 2, -modelSizeVector.y / 2, -12, 0.3);
            this.shadowCamera.name = 'Shadow Camera';
            this.shadowCamera.rotation.x = Math.PI / 2;

            this.depthMaterial = new THREE.MeshDepthMaterial();
            this.depthMaterial.userData['darkness'] = { value: 1 };
            this.depthMaterial.onBeforeCompile = (shader) => {
              shader.uniforms['darkness'] = this.depthMaterial?.userData['darkness'];
              shader.fragmentShader = `uniform float darkness;
                ${shader.fragmentShader.replace(
                  'gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );',
                  'gl_FragColor = vec4( vec3( 0.0 ), ( 1.0 - fragCoordZ ) * darkness );'
                )}`;
            };
            this.depthMaterial.depthTest = false;
            this.depthMaterial.depthWrite = false;

            this.horizontalBlurMaterial = new THREE.ShaderMaterial(HorizontalBlurShader);
            this.horizontalBlurMaterial.depthTest = false;

            this.verticalBlurMaterial = new THREE.ShaderMaterial(VerticalBlurShader);
            this.verticalBlurMaterial.depthTest = false;

            this.addShadows(boundingBoxes, originalModelSizeVector, this.modelGroup, this.blurPlane, this.shadowCamera, this.renderTarget);

            assert(this.scene, 'No scene');
            this.scene.add(this.modelGroup);

            const boundingBoxForOverlay = new THREE.Box3().setFromObject(this.model.scene);
            this.addOverlay(boundingBoxForOverlay, overlayButtons);

            if (this.camera) {
              this.setupNormalCamera();
            }

            assert(this.orbitControls, 'No orbit controls');
            const previousDidUserPan = this.didUserPan;
            if (this.shouldDoInitialCameraReposition) {
              if (!this.isSharing()) {
                this.orbitControls.setAzimuthalAngle(1.57);
                this.orbitControls.setPolarAngle(0);
                this.orbitControls.update();
              } else {
                this.orbitControls?.setPolarAngle(0.57);
                this.orbitControls?.setAzimuthalAngle(-0.41);
                this.orbitControls.update();
              }
            } else {
              this.orbitControls.update();
            }
            this.didUserPan = previousDidUserPan;

            // this.camera.position.multiplyScalar(farE);
            // this.camera.updateProjectionMatrix();
            // this.camera.lookAt(0,0,0);
            // this.orbitControls.update();


            // this.mainImageLoaded();

            // if (this.isSharing()) {
            //   this.threeAnimate();
            //   this.setPrintImage();
            // }

            // this.shouldDoInitialCameraReposition = false;
            // this.renderer.setAnimationLoop(this.threeAnimate.bind(this));
          } catch (e) {
            console.error(e);
            didModelSucceeed = false;
          }
        } else {
          assert(configuredItem.primaryMaterial, 'Primary material was null');
          this.renderer?.setAnimationLoop(null);
          const modelMeshes: any[] = [];
          this.model.scene.traverse((descendant: any) => {
            if (descendant.isMesh) {
              const material = new descendant.material.constructor();
              material.copy(descendant.material);
              descendant.material.dispose();
              descendant.material = material;
              modelMeshes.push(descendant);
            }
          });

          if (changes.some(c => c.toLowerCase().includes('material'))) {
            this.setMaterialsColor(modelMeshes, configuredItem);
          }

          if (changes.includes('design') || changes.some(c => c.toLowerCase().includes('material')) || changes.some(c => c.toLowerCase().includes('gemstone'))) {
            const itemAssetsToUse = itemAssets.filter(itemAsset => (!configuredItem.gemstoneArrangement && prevItem.gemstoneArrangement && itemAsset.location === 'Outside') || !this.previousItemAssets.some(previousItemAsset => previousItemAsset.fileName === itemAsset.fileName && previousItemAsset.location === itemAsset.location && previousItemAsset.type === itemAsset.type));
            const itemAssetsToExclude = itemAssets.filter(itemAsset => this.previousItemAssets.some(previousItemAsset => !(!configuredItem.gemstoneArrangement && prevItem.gemstoneArrangement && itemAsset.location === 'Outside') && previousItemAsset.fileName === itemAsset.fileName && previousItemAsset.location === itemAsset.location && previousItemAsset.type === itemAsset.type));
            assert(this.scene, 'Scene is null');

            const indexMap = ['Outside'];
            if (modelMeshes.length === 3) {
              indexMap.push('Edge');
            }
            indexMap.push('Inside');

            const appliedTexturesToDisposeOnly: THREE.Texture[] = [];
            modelMeshes.forEach((m, i) => {
              const location = indexMap[i];
              const excludedItemAssetsForLocation = itemAssetsToExclude.filter(ia => ia.location === location);
              const excludedMaterialTextureKeys = excludedItemAssetsForLocation.flatMap(itemAssetForLocation => [ItemAssetLocationToThreeJSKeyMap[itemAssetForLocation.type]].flat());
              const newItemAssetsForLocation = itemAssetsToUse.filter(ia => ia.location === location);
              const newmaterialTextureKeys = newItemAssetsForLocation.flatMap(newItemAssetForLocation => [ItemAssetLocationToThreeJSKeyMap[newItemAssetForLocation.type]].flat());

              Object.keys(m.material).forEach(materialKey => {
                if (m.material[materialKey]?.isTexture) {
                  if (newmaterialTextureKeys.includes(materialKey)) {
                    appliedTexturesToDisposeOnly.push(m.material[materialKey]);
                  } else if (!excludedMaterialTextureKeys.includes(materialKey)) {
                    m.material[materialKey].dispose();
                    m.material[materialKey] = null;
                  }
                }
              });
            });

            await this.addTextures(modelMeshes, itemAssetsToUse);

            appliedTexturesToDisposeOnly.forEach(t => t.dispose());

            // this.renderer?.setAnimationLoop(this.threeAnimate.bind(this));
          }
        }
      }

      if (gltfFileOverride || changes.some(k => !k.includes('size'))) {
        const modelMeshes: any[] = [];
        this.model.scene.traverse((descendant: any) => {
          if (descendant.isMesh) {
            modelMeshes.push(descendant);
          }
        });

        if (configuredItem.gemstoneType && changes.some(k => !k.includes('engraving'))) {
          const itemAssetsForGemstones = (itemAssets ?? this.previousItemAssets)?.filter(itemAsset => itemAsset.location === 'Outside') ?? [];
            const width = Math.round((new THREE.Box3().setFromObject(modelMeshes[0]).getSize(new THREE.Vector3).y + Number.EPSILON) * 10 / 5) / 10 * 5;
            await this.generateSettingTextures2(modelMeshes[0].material, configuredItem, width, itemAssetsForGemstones);
            this.removeStones(this.model);
            await this.addStones(configuredItem, this.model);
        }

        if (configuredItem.engravingText && configuredItem.engravingType) {
          const itemAssetsForEngraving = (itemAssets ?? this.previousItemAssets)?.filter(itemAsset => itemAsset.location === 'Inside') ?? [];
          const changedItemAssetsForEngraving = (itemAssets ?? this.previousItemAssets)?.filter(itemAsset => !this.previousItemAssets.some(previousItemAsset => previousItemAsset.fileName === itemAsset.fileName && previousItemAsset.location === itemAsset.location && previousItemAsset.type === itemAsset.type)) ?? [];
          const itemAssetsToUseForEngraving = changes.some(k => k.includes('engraving')) ? itemAssetsForEngraving : changedItemAssetsForEngraving;

          if (itemAssetsToUseForEngraving.length > 0) {
            // C1/C2 (b): same join as createModel -- the engraving path reads
            // insideTextureCache, so wait for the prefetch kicked off at the top
            // of loadModel before drawing from the cache.
            await pendingInsideTextureCache;
            await this.addEngravingTextures(modelMeshes, itemAssetsToUseForEngraving, configuredItem);
          }
        } else if (changes.some(k => k.includes('engraving'))) {
          const insideItemAssets = (itemAssets ?? this.previousItemAssets)?.filter(itemAsset => itemAsset.location === 'Inside') ?? [];
          const indexMap = this.getTextureLocationIndexMap(modelMeshes.length);
          const index = indexMap.findIndex(i => i === 'Inside');
          const textureKeysInItemAssets = insideItemAssets.flatMap(asset => [ItemAssetLocationToThreeJSKeyMap[asset.type]].flat());
          const texturesKeysToRemove = Object.keys(modelMeshes[index].material).filter((key: any) => !textureKeysInItemAssets.includes(key) && modelMeshes[index].material[key] instanceof THREE.Texture);
          texturesKeysToRemove.forEach(key => {
            modelMeshes[index].material[key] = null;
          });
          await this.addTextures(modelMeshes, insideItemAssets);
        }

        this.prevItem = Object.assign({}, configuredItem);
        if (changes.some(k => !k.includes('engraving'))) {
          this.previousItemAssets = itemAssets ?? [];
        }

        if (didCreateModel) {
          this.mainImageLoaded();

          if (this.isSharing()) {
            this.threeAnimate();
            this.setPrintImage();
          }

          this.shouldDoInitialCameraReposition = false;
        }
      }

      this.renderer?.setAnimationLoop(this.threeAnimate.bind(this));
      this.loadingService.done('main-image:viewer');
      this.modelCreated.emit(didModelSucceeed);

      if (this.mainImageService.shouldMakeGemstoneSectionActive) {
        this.makeSectionActive('Gemstones');
        this.mainImageService.shouldMakeGemstoneSectionActive = false;
      }
    }
  }

  async cacheInsideTextures(itemAssets: ItemAsset[]) {
    const insideAssets = itemAssets.filter(ia => ia.location === 'Inside');
    const promises = insideAssets.map(async (itemAssetForLocation) => {
      const cachedAsset = this.insideTextureCache.get(itemAssetForLocation.type)
      if (itemAssetForLocation.location && (!cachedAsset || cachedAsset.fileName !== itemAssetForLocation.fileName)) {
        const image = await this.getImageWithURL(getAssetURL(itemAssetForLocation.fileName));
        this.insideTextureCache.set(itemAssetForLocation.type, Object.assign({}, itemAssetForLocation, { image }));
      }
      return true;
    });
    await Promise.all(promises);
  }

  async addEngravingTextures(modelMeshes: any[], itemAssets: ItemAsset[], configuredItem: ConfiguredItem) {
    const indexMap = this.getTextureLocationIndexMap(modelMeshes.length);
    const index = indexMap.findIndex(m => m === 'Inside');

    const width = Math.round((new THREE.Box3().setFromObject(modelMeshes[index]).getSize(new THREE.Vector3).y + Number.EPSILON) * 10 / 5) / 10 * 5;

    const engravingTextures = (await new Promise<{ key: string, texture: THREE.Texture}[]>((res) => {
      requestAnimationFrame(async () => {
        const engravingTextures = await this.generateEngravingTextures(itemAssets, configuredItem, width);
        res(engravingTextures);
      });
    }));

    engravingTextures.forEach(({key, texture}) => {
      modelMeshes[index].material[key] = texture;
      texture.dispose();
    });
    modelMeshes[index].material.needsUpdate = true;
  }

  async generateEngravingTextures(itemAssets: ItemAsset[], configuredItem: ConfiguredItem, primaryMaterialWidth: number) {
    const insideAssets = itemAssets.filter(asset => asset.location === 'Inside') ?? [];
    const font = configuredItem.engravingType?.shortName === 'BLK' ? 'engraving_block' : 'engraving_script';
    const text = `${configuredItem.engravingText}`;
    assert(text, 'No engraving text');
    assert(primaryMaterialWidth, 'Could not determine engraving texture width');
    const { width, height, fontScript, fontBlock } = this.getTextureWidthForWidth(primaryMaterialWidth);
    const fontSize = (configuredItem.engravingType?.shortName === 'BLK' ? fontBlock : fontScript) * 100;

    this.baseTextCanvas.width = width;
    this.baseTextCanvas.height = height;

    const baseTextContext = this.baseTextCanvas.getContext('2d');
    assert(baseTextContext, 'Failed to get context for canvas');
    baseTextContext.imageSmoothingEnabled = true;
    baseTextContext.imageSmoothingQuality = 'high';
    baseTextContext.font = `${fontSize}px ${font}`;
    baseTextContext.textBaseline = 'top';
    baseTextContext.fillStyle = 'black';

    const startOffset = height - baseTextContext.measureText(text).width - 100;

    baseTextContext.rotate(90 * Math.PI / 180);
    baseTextContext.scale(1, -1);
    baseTextContext.fillText(text, startOffset - 0, (width - fontSize) / 2);

    const textures: {key: string, texture: THREE.Texture}[] = [];

    const mapAsset = insideAssets.find(asset => asset.type === 'Base Color Texture');
    const mapImage = this.insideTextureCache.get('map')?.image;
    if (mapAsset && mapImage) {
      const mapCanvasWidth = 2048;
      const mapCanvasHeight = 2048;
      this.mapCanvas.width = mapCanvasWidth;
      this.mapCanvas.height = mapCanvasHeight;
      const mapContext = this.mapCanvas.getContext('2d');
      assert(mapContext, 'Failed to get context for canvas');
      const image = mapImage;// await this.getImageWithURL(getAssetURL(mapAsset.fileName));
      mapContext.imageSmoothingEnabled = false;

      this.tmpCanvas = this.tmpCanvas as HTMLCanvasElement;
      this.tmpCanvas.width = mapCanvasWidth;
      this.tmpCanvas.height = mapCanvasHeight;
      const tmpContext = this.tmpCanvas.getContext('2d');
      assert(tmpContext, 'No context for canvas');
      tmpContext.fillStyle = '#ffffff86';
      tmpContext.drawImage(this.baseTextCanvas, 0, 0, mapCanvasWidth, mapCanvasHeight);
      tmpContext.fillRect(0, 0, mapCanvasWidth, mapCanvasHeight);

      mapContext.drawImage(image, 0, 0, mapCanvasWidth, mapCanvasHeight);
      mapContext.drawImage(this.tmpCanvas, 0, 0, mapCanvasWidth, mapCanvasHeight);

      textures.push({ key: 'map', texture: new THREE.CanvasTexture(this.mapCanvas)});
    }

    const normalCanvasWidth = 2048;
    const normalCanvasHeight = 2048;
    this.normalCanvas.width = normalCanvasWidth;
    this.normalCanvas.height = normalCanvasHeight;
    const normalContext = this.normalCanvas.getContext('2d');
    assert(normalContext, 'No context for canvas');

    normalContext.filter = 'blur(3px)';
    normalContext.fillStyle = 'white';
    normalContext.fillRect(0, 0, normalCanvasWidth, normalCanvasHeight);
    normalContext.drawImage(this.baseTextCanvas, 0, 0, normalCanvasWidth, normalCanvasHeight);

    if (!this.normalConverter) {
      this.normalConverter = new HeightToNormal();
    }
    this.normalConverter.apply(this.normalCanvas, {
      blurSharp: 0,
      level: 8,
      strength: 1,
      invertedRed: false,
      invertedGreen: false,
      invertedSource: true,
      type: NORMA_MAP_TYPES.SOBEL,
    });

    textures.push({ key:'normalMap', texture: new THREE.CanvasTexture(this.normalConverter.canvas)});

    const pbrAsset = insideAssets.find(asset => asset.type === 'Metallic/Roughness Texture');
    const pbrImage = this.insideTextureCache.get('Metallic/Roughness Texture')?.image;
    if (pbrAsset && pbrImage) {
      baseTextContext.reset();
      baseTextContext.clearRect(0, 0, width, height);
      baseTextContext.imageSmoothingEnabled = true;
      baseTextContext.imageSmoothingQuality = 'high';
      baseTextContext.font = `${fontSize}px ${font}`;
      baseTextContext.textBaseline = 'top';
      baseTextContext.fillStyle = '#00FFFF';
      baseTextContext.rotate(90 * Math.PI / 180);
      baseTextContext.scale(1, -1);
      baseTextContext.fillText(text, startOffset - 0, (width - fontSize) / 2);

      const pbrCanvasWidth = 2048;
      const pbrCanvasHeight = 2048;
      this.pbrCanvas.width = pbrCanvasWidth;
      this.pbrCanvas.height = pbrCanvasHeight;
      const pbrContext = this.pbrCanvas.getContext('2d');
      assert(pbrContext, 'No context for canvas');
      pbrContext.imageSmoothingEnabled = false;
      if (pbrAsset) {
        const image = pbrImage; //await this.getImageWithURL(getAssetURL(pbrAsset.fileName));
        pbrContext.drawImage(image, 0, 0, pbrCanvasWidth, pbrCanvasHeight);
      } else {
        pbrContext.fillRect(0, 0, pbrCanvasWidth, pbrCanvasHeight);
      }
      pbrContext.drawImage(this.baseTextCanvas, 0, 0, pbrCanvasWidth, pbrCanvasHeight);

      const pbrTexture = new THREE.CanvasTexture(this.pbrCanvas);
      textures.push({ key:'roughnessMap', texture: pbrTexture});
      textures.push({ key:'metalnessMap', texture: pbrTexture});
    }

    textures.forEach(texture => {
      if (this.renderer) {
        texture.texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
        texture.texture.dispose();
      }
    });
    return textures;
  }

  removeStones(model: any) {
    const stones = Array.from(this.scene?.children?.filter(c => !!c && c.name === 'Gemstones') ?? []);
    stones.forEach(s => s.removeFromParent());
    model?.scene.children[0].children[0].rotation.set(0,0,0);
  }

  async addStones(configuredItem: ConfiguredItem, model: any, stoneGroupParent?: any) {
    const arrangement = configuredItem.gemstoneArrangement;
    assert(arrangement, 'No gemstone arrangement selected');

    const carat = configuredItem.gemstoneSize;
    assert(carat, 'No gemstone carat selected');

    const stoneType = configuredItem.gemstoneType;
    assert(stoneType, 'No gemstone type selected');

    const stoneShape = configuredItem.gemstoneShape;
    assert(stoneShape, 'No gemstone shape selected');

    const stoneDetails = this.getStoneDetailsForCarat(carat.value);
    // const maxStoneCount = stoneDetails.maxCount;

    // const count = this.getStoneCountForArrangement(arrangement.displayName, maxStoneCount);

    const size10 = this.sizesService.values().find(s => s.value === 10);
    const configuredItemForRendering = Object.assign({}, configuredItem, { size: size10 });
    const maxStoneCount: number = await this.stoneCountsProvider.getMax(configuredItemForRendering);
    const maxStoneCountForSetting: number = await this.stoneCountsProvider.getMaxBySetting(configuredItemForRendering);
    const count = await this.stoneCountsProvider.get(configuredItemForRendering);

    const diamondModel = await this.gltfLoader.loadAsync(`/assets/${carat.value * 100}pt-${stoneShape.displayName}-Stone.glb`);
    const expectedSize = (diamondModel.scene.children[0].children[0] as any).geometry.boundingBox.getSize(new THREE.Vector3());
    const actualSize = new THREE.Box3().setFromObject(diamondModel.scene).getSize(new THREE.Vector3());
    diamondModel.scene.scale.multiplyScalar(expectedSize.x / actualSize.x)

    // const modelBoundingBox = model.scene.children[0].children[0].geometry.boundingBox;
    const modelBoundingBox = new THREE.Box3().setFromObject(model.scene.children[0].children[0], true);

    const rotationOrigin = { x: 0, z: Math.PI / 2 };

    const environment = this.scene?.environment;
    assert(environment, 'No envrionment file');

    const camera = this.camera;
    assert(camera, 'No camera');

    const stonesGroup = new THREE.Group();
    stonesGroup.name = 'Gemstones';

    diamondModel.scene.scale.z = 800;

    const diamondSize = new THREE.Box3().setFromObject(diamondModel.scene, true).getSize(new THREE.Vector3());
    const modelSize = modelBoundingBox.getSize(new THREE.Vector3());
    const modelRadius = Math.max(modelSize.x / 2, modelSize.y / 2);
    const zOff = -1 * diamondSize.z - (configuredItem.form?.shortName !== 'RDCM' ? 0 : 0.1);//diamondSize.z * -1 - 0.12;//stoneDetails.zOffset;

    const designAreaBounds = model.scene.children[0].children[0].geometry.boundingBox.getSize(new THREE.Vector3());
    const axes = ['x', 'y', 'z'];
    const axisValues = [designAreaBounds.x, designAreaBounds.y, designAreaBounds.z];
    const rotationAxisValue = Math.min(...axisValues);
    const rotationAxis = axes[axisValues.findIndex(v => rotationAxisValue === v)];
    const stoneWidth = diamondSize.x;
    const offset = ((stoneWidth / 2 * (count - 1))) / (stoneWidth * maxStoneCountForSetting) * Math.PI * 2
    stonesGroup.rotation.z = Math.PI
    stonesGroup.rotation.y = configuredItem.gemstoneArrangement?.displayName === 'Half' && configuredItem.gemstoneSetting?.displayName === 'Burnish' ? 0 : stoneDetails.stoneRotationOrigin * Math.PI / 180 - offset;

    const textureOffset = stoneDetails.textureOffset.get(`${configuredItem.gemstoneSetting?.displayName ?? ''}-${configuredItem.gemstoneArrangement?.displayName ?? ''}`) ?? 0
    model.scene.children[0].children[0].rotation[rotationAxis] = stoneDetails.outsideRotation * Math.PI / 180 - offset - textureOffset * Math.PI / 180;

    for (let i = 0; i < count; i++) {
      const stoneToAdd = i === 0 ? diamondModel.scene : diamondModel.scene.clone(true);

      stoneToAdd.name = `stone-${i}`;

      const ds: any = stoneToAdd.children[0].children[0];
      const bMesh = ds.geometry.computeBoundsTree()

      const material = new DiamondShaderMaterial(environment, camera);
      material.uniforms['bvh'].value.updateFrom(bMesh)
      ds.material = material;

      if (stoneType.displayName.toLocaleLowerCase().includes('black diamond')) {
        ds.material.uniforms.color.value.set(0.02,0.02,0.02)
        ds.material.uniforms.aberrationStrength.value = 0
        ds.material.uniforms.ior.value = 1.3
        ds.material.uniforms.fresnel.value = 50
      } else if (stoneType.displayName.toLowerCase().includes('sapphire')) {
        ds.material.uniforms.color.value.set(0.002, 0.015, 0.075);
        ds.material.uniforms.ior.value = 1.6;
        ds.material.uniforms.fresnel.value = 1;
        ds.material.uniforms.aberrationStrength.value = 0;
        ds.material.uniforms.bounces.value = 3;
      }
      ds.material.needsUpdate = true

      const theta = configuredItem.gemstoneArrangement?.displayName === 'Half' && configuredItem.gemstoneSetting?.displayName === 'Burnish' ? i / (count - 1) * Math.PI: (2 * Math.PI * i - zOff / 2) / maxStoneCountForSetting
      const x = (modelRadius + zOff) * Math.cos(theta);
      const y = (modelRadius + zOff) * Math.sin(theta);
      stoneToAdd.position.set(x, 0, y);
      stoneToAdd.updateWorldMatrix(true, true)

      stoneToAdd.rotation.set(rotationOrigin.x, rotationOrigin.z, 0);
      const v1 = new THREE.Vector3().copy(stoneToAdd.position).normalize();
      stoneToAdd.rotation.y = -1 * Math.atan2(v1.z, v1.x) + rotationOrigin.z;

      stonesGroup?.add(stoneToAdd);
    }
    if (stoneGroupParent) {
      stoneGroupParent.add(stonesGroup);
    } else {
      this.scene?.add(stonesGroup);
    }

    return stonesGroup;
  }

    getStoneDetailsForCarat(carat: number) {
    const map: Record<number, { maxCount: number, zOffset: number, outsideRotation: number, stoneRotationOrigin: number, textureOffset: Map<string, number> }> = {
      0.01: { maxCount: 40, zOffset: -2, outsideRotation: 250, stoneRotationOrigin: 88, textureOffset: new Map([['Burnish-Single', -1], ['Burnish-Half', -1.5], ['Burnish-Eternity', -1]]) },
      0.02: { maxCount: 40, zOffset: -1.2, outsideRotation: 251.5, stoneRotationOrigin: 89.5, textureOffset: new Map([['Burnish-Single', 0], ['Burnish-Half', 1], ['Burnish-Eternity', -0.5], ['Channel-Third', 1.2], ['Channel-Half', 1.2], ['Channel-Eternity', -1], ['Pave-Third', 1], ['Pave-Half', 1], ['Pave-Eternity', 1.5]]) },
      0.04: { maxCount: 40, zOffset: -2, outsideRotation: 248.5, stoneRotationOrigin: 88, textureOffset: new Map([['Burnish-Single', -1.5], ['Burnish-Half', -0.5], ['Burnish-Eternity', -1.5], ['Channel-Half', 0.3], ['Pave-Eternity', 1], ['Pave-Half', 0.5]]) },
      0.06: { maxCount: 40, zOffset: -2, outsideRotation: 248, stoneRotationOrigin: 88, textureOffset: new Map([['Burnish-Single', -2], ['Burnish-Half', -0.5], ['Burnish-Eternity', -1.8]]) },
      0.08: { maxCount: 26, zOffset: -2, outsideRotation: 247.5, stoneRotationOrigin: 88, textureOffset: new Map([['Burnish-Single', -2], ['Burnish-Half', -0.5], ['Burnish-Eternity', -2]]) },
    };
    return map[carat];
  }
  async generateSettingTextures2(outsideMaterial: THREE.MeshStandardMaterial, configuredItem: ConfiguredItem, primaryMaterialWidth: number, itemAssets: ItemAsset[]) {
    const { width, height } = this.getTextureWidthForWidth(primaryMaterialWidth);

    const mapForBounds = (outsideMaterial.map ?? outsideMaterial.displacementMap ?? outsideMaterial.aoMap ?? outsideMaterial.normalMap ?? outsideMaterial.roughnessMap);
    const defaultBufferWidth = mapForBounds?.image?.width ?? 2048;
    const defaultBufferHeight = mapForBounds?.image?.height ?? 2048;

    const normalMap = itemAssets.find(a => a.type === 'Normal Texture') ?? { fileName: 'POLISHED-N-ALL.1740787460259.webp' };
    const mrMap = itemAssets.find(a => a.type === 'Metallic/Roughness Texture') ?? { fileName: null };
    const aoMap = itemAssets.find(a => a.type === 'Ambient Occlusion Texture') ?? { fileName: null };
    const displacementMap = itemAssets.find(a => a.type === 'Displacement Texture') ?? { fileName: null };
    const baseColorMap = itemAssets.find(a => a.type === 'Base Color Texture') ?? { fileName: null };

    const cBGBuffer = document.createElement('canvas');
    cBGBuffer.width = outsideMaterial.map?.image?.width ?? defaultBufferWidth;
    cBGBuffer.height = outsideMaterial.map?.image?.height ?? defaultBufferHeight;
    const cBGBufferContext = cBGBuffer.getContext('2d');
    assert(cBGBufferContext, 'Failed to get context for canvas');
    if (baseColorMap.fileName) {
      cBGBufferContext.clearRect(0, 0, cBGBuffer.width, cBGBuffer.height);
    } else {
      cBGBufferContext.fillStyle = 'white';
      cBGBufferContext.fillRect(0, 0, cBGBuffer.width, cBGBuffer.height);
    }

    const nBGBuffer = document.createElement('canvas');
    nBGBuffer.width = outsideMaterial.normalMap?.image?.width ?? defaultBufferWidth;
    nBGBuffer.height = outsideMaterial.normalMap?.image?.height ?? defaultBufferHeight;
    const nBGBufferContext = nBGBuffer.getContext('2d');
    assert(nBGBufferContext, 'Failed to get context for canvas');
    nBGBufferContext.clearRect(0, 0, nBGBuffer.width, nBGBuffer.height);

    const mrBGBuffer = document.createElement('canvas');
    mrBGBuffer.width = outsideMaterial.roughnessMap?.image?.width ?? defaultBufferWidth;
    mrBGBuffer.height = outsideMaterial.roughnessMap?.image?.height ?? defaultBufferHeight;
    const mrBGBufferContext = mrBGBuffer.getContext('2d');
    assert(mrBGBufferContext, 'Failed to get context for canvas');
    mrBGBufferContext.clearRect(0, 0, mrBGBuffer.width, mrBGBuffer.height);

    const dBGBuffer = document.createElement('canvas');
    dBGBuffer.width = outsideMaterial.displacementMap?.image?.width ?? defaultBufferWidth;
    dBGBuffer.height = outsideMaterial.displacementMap?.image?.height ?? defaultBufferHeight;
    const dBGBufferContext = dBGBuffer.getContext('2d');
    assert(dBGBufferContext, 'Failed to get context for canvas');
    dBGBufferContext.clearRect(0, 0, dBGBuffer.width, dBGBuffer.height);

    const oBGBuffer = document.createElement('canvas');
    oBGBuffer.width = outsideMaterial.alphaMap?.image?.width ?? defaultBufferWidth;
    oBGBuffer.height = outsideMaterial.alphaMap?.image?.height ?? defaultBufferHeight;
    const oBGBufferContext = oBGBuffer.getContext('2d');
    assert(oBGBufferContext, 'Failed to get context for canvas');
    oBGBufferContext.fillStyle = 'white';
    oBGBufferContext.fillRect(0, 0, oBGBuffer.width, oBGBuffer.height);

    const aoBGBuffer = document.createElement('canvas');
    aoBGBuffer.width = outsideMaterial.aoMap?.image?.width ?? defaultBufferWidth;
    aoBGBuffer.height = outsideMaterial.aoMap?.image?.height ?? defaultBufferHeight;
    const aoBGBufferContext = aoBGBuffer.getContext('2d');
    assert(aoBGBufferContext, 'Failed to get context for canvas');
    aoBGBufferContext.clearRect(0, 0, aoBGBuffer.width, aoBGBuffer.height);
    if (aoMap.fileName) {
      aoBGBufferContext.clearRect(0, 0, aoBGBuffer.width, aoBGBuffer.height);
    } else {
      aoBGBufferContext.fillStyle = 'white';
      aoBGBufferContext.fillRect(0, 0, aoBGBuffer.width, aoBGBuffer.height);
    }

    const arrangement = configuredItem.gemstoneArrangement;
    assert(arrangement, 'No gemstone arrangement selected');

    const stoneSetting = configuredItem.gemstoneSetting;
    assert(stoneSetting, 'No gemstone setting selected');

    const carat = configuredItem.gemstoneSize;
    assert(carat, 'No gemstone carat selected');


    const size10 = this.sizesService.values().find(s => s.value === 10);
    const configuredItemForRendering = Object.assign({}, configuredItem, { size: size10 });
    const maxStoneCount: number = await this.stoneCountsProvider.getMax(configuredItemForRendering);
    const maxStoneCountBySetting: number = await this.stoneCountsProvider.getMaxBySetting(configuredItemForRendering);
    const stoneCount = await this.stoneCountsProvider.get(configuredItemForRendering);
    // const maxStoneCount: number = 26; // 8pt
    // const maxStoneCount: number = await this.stoneCountsProvider.getMax(configuredItem); //this.getStoneDetailsForCarat(carat.value).maxCount; // 2pt
    // const stoneCount = await this.stoneCountsProvider.get(configuredItem); //this.getStoneCountForArrangement(arrangement.displayName, maxStoneCount); //arrangement.displayName.toLowerCase().includes('eternity') ? maxStoneCount : +(arrangement.displayName.match(/^(?<count>\d+)/)?.groups?.['count'] ?? NaN);
    const setting: SettingType = configuredItem.gemstoneSetting?.displayName.includes('Channel') ? 'Channel' : (configuredItem.gemstoneSetting?.displayName.includes('Pave') ? 'Pave' : 'Burnish');
    // const size = 8;
    const size = carat.value * 100;// 2;

    const featureBaseKey = `${size}pt-${setting}`

    const middleFeatures = await Promise.all(['C', 'N', 'D', 'R', 'O', 'A'].map(textureType => this.getImageWithURL(`/assets/${featureBaseKey}-middle-${textureType}.webp`))); // get from imageAssets
    const endFeatures = (!['Channel', 'Pave'].includes(setting) || maxStoneCountBySetting === stoneCount) ? middleFeatures : await Promise.all(['C', 'N', 'D', 'R', 'O', 'A'].map(textureType => this.getImageWithURL(`/assets/${featureBaseKey}-end-${textureType}.webp`))); // get from imageAssets

    // const [middleColor, middleNormal, middleDisplacement, middleMR, middleAlpha, middleAmbientOcclusion] = middleFeatures;
    // const [endColor, endNormal, endDisplacement, endMR, endAlpha, endAmbientOcclusion] = endFeatures;
    const bufferContexts = [cBGBufferContext, nBGBufferContext, dBGBufferContext, mrBGBufferContext, oBGBufferContext, aoBGBufferContext];
    const settingFeatures = middleFeatures.map((middleFeature, i) => ({ middleFeature, endFeature: endFeatures[i], bufferContext: bufferContexts[i] }));

    // const resizedImageHeight = Math.ceil(height / maxStoneCount);
    // const resizedImageWidth = Math.ceil(resizedImageHeight * endNormal.width / endNormal.height);

    // const textureScaledWidth = resizedImageWidth / width * bufferWidth;
    // const textureScaledHeight = resizedImageHeight / height * bufferHeight;

    // const xStart = bufferWidth / 2 - textureScaledWidth / 2;
    // // const yStart = bufferHeight - 40 //bufferHeight / 2 - textureScaledHeight / 2;
    // const yStart = 0// bufferHeight / 2 - textureScaledHeight / 2;

    for (let i = 0; i < stoneCount; i++) {
      settingFeatures.forEach((features) => {
        const resizedImageHeight = Math.ceil(height / maxStoneCount);
        const resizedImageWidth = Math.ceil(resizedImageHeight * features.middleFeature.width / features.middleFeature.height);

        const textureScaledWidth = resizedImageWidth / width * features.bufferContext.canvas.width;
        const textureScaledHeight = resizedImageHeight / height * features.bufferContext.canvas.height;

        const xStart = features.bufferContext.canvas.width / 2 - textureScaledWidth / 2;
        // const yStart = bufferHeight - 40 //bufferHeight / 2 - textureScaledHeight / 2;
        const yStart = 0// bufferHeight / 2 - textureScaledHeight / 2;

        var leadingMargin = 0;
        if (setting === 'Burnish') {
          // if (arrangement.displayName === 'Half') {
            // leadingMargin = (features.bufferContext.canvas.height / 2 - textureScaledHeight * (stoneCount - 1)) / (stoneCount - 1);
            // console.log(leadingMargin)
          // } else {
            leadingMargin = (features.bufferContext.canvas.height - textureScaledHeight * maxStoneCountBySetting) / maxStoneCountBySetting;
          // }
        }

        const x = xStart;
        const y = (yStart + i * (textureScaledHeight + leadingMargin)) % features.bufferContext.canvas.height;
        // const y = stoneCount === 1 ? features.bufferContext.canvas.height / 2 - (yStart + textureScaledHeight + i * (textureScaledHeight + leadingMargin)) % features.bufferContext.canvas.height : (yStart + i * (textureScaledHeight + leadingMargin)) % features.bufferContext.canvas.height;

        let featureToRender: HTMLImageElement | HTMLCanvasElement = features.middleFeature;
        if (i === 0) {
          featureToRender = features.endFeature;
        } else if (i === stoneCount - 1) {
          featureToRender = this.flipImage(features.endFeature);
        }

        features.bufferContext.drawImage(featureToRender, x, y, textureScaledWidth, textureScaledHeight);
      });
    }

    const [normalImage, mrImage, aoImage, displacementImage, baseColorImage] = await Promise.all([normalMap, mrMap,  aoMap, displacementMap, baseColorMap].map((a) => !a.fileName ? undefined : this.getImageWithURL(getAssetURL(a.fileName))));

    const finalC = this.getCompositedTexture(cBGBuffer, baseColorImage);
    const finalN = this.getCompositedTexture(nBGBuffer, normalImage);
    const finalD = this.getCompositedTexture(dBGBuffer, displacementImage);
    const finalR = this.getCompositedTexture(mrBGBuffer, mrImage);
    const finalO = this.getCompositedTexture(oBGBuffer, undefined);
    const finalAo = this.getCompositedTexture(aoBGBuffer, aoImage);

    const [color, normal, displacement, roughness, alpha, ambientOcclusion] = await Promise.all([finalC, finalN, finalD, finalR, finalO, finalAo].map(async (canvas) => {
      const texture = await (new THREE.TextureLoader()).loadAsync(canvas.toDataURL('image/jpeg'));
      texture.anisotropy = this.renderer?.capabilities.getMaxAnisotropy() ?? 1;
      texture.dispose();
      return texture;
    }));


// document.body.append(color.image)
// document.body.append(normal.image)
// document.body.append(displacement.image)
// document.body.append(roughness.image)
// document.body.append(roughness.image)
// document.body.append(alpha.image)
// document.body.append(ambientOcclusion.image)

    outsideMaterial.map = color;
    outsideMaterial.normalMap = normal;
    outsideMaterial.displacementMap = displacement;
    outsideMaterial.roughnessMap = roughness;
    outsideMaterial.metalnessMap = roughness;
    // outsideMaterial.alphaMap = alpha;
    outsideMaterial.aoMap = ambientOcclusion;
    // ADO #9834: `outsideMaterial.alphaTest = 1` was here, and nothing ever set it
    // back. Nothing resets scalars — the loop that cleans up after a gemstone
    // change only clears texture-valued properties, and every update copies the
    // material forward with material.copy() — so it survived into the no-stones
    // state and discarded the near-opaque texels some outside Base-Color maps
    // carry, which render as seams across the band. It did nothing here either
    // way: the composited buffers above are JPEG-encoded, and JPEG has no alpha,
    // so the test could never fire while alphaMap is commented out.
    // 🛑 If you restore the alphaMap line, alphaTest has to come back with it (an
    // alphaMap does nothing at alphaTest 0 with transparent false) — fix the
    // scalar reset first or this bug returns. Do not "reset it to 0" instead:
    // GLTFLoader sets alphaTest from the model's alphaMode/alphaCutoff, so a
    // hard 0 would clobber a model-authored cutoff.
    outsideMaterial.needsUpdate = true;
  }

  flipImage(image: HTMLImageElement | HTMLCanvasElement) {
    const flipBuffer = document.createElement('canvas');
    flipBuffer.width = image.width;
    flipBuffer.height = image.height;
    const flipBufferContext = flipBuffer.getContext('2d');
    assert(flipBufferContext, 'Failed to get context for canvas');
    flipBufferContext.clearRect(0, 0, flipBuffer.width, flipBuffer.height);
    flipBufferContext.scale(1, -1);
    flipBufferContext.translate(0, flipBuffer.height * -1);
    flipBufferContext.drawImage(image, 0, 0);

    return flipBuffer;
  }

  getClippedRemainderY(image: HTMLImageElement | HTMLCanvasElement, remainder: number) {
    const clipBuffer = document.createElement('canvas');
    document.body.append(image)
    document.body.append(clipBuffer)
    clipBuffer.width = image.width;
    clipBuffer.height = remainder;
    const clipBufferContext = clipBuffer.getContext('2d');
    assert(clipBufferContext, 'Failed to get context for canvas');
    clipBufferContext.clearRect(0, 0, clipBuffer.width, clipBuffer.height);
    clipBufferContext.drawImage(image, 0, remainder * -1);

    return clipBuffer;
  }

  async getNormalTexture(features: TextureFeature[], existingTexture: HTMLCanvasElement) {
    const buffer = document.createElement('canvas');
    buffer.width = existingTexture.width;
    buffer.height = existingTexture.height;

    const bufferContext = buffer.getContext('2d');
    assert(bufferContext, 'No context for buffer');
    bufferContext.fillStyle = 'black';
    bufferContext.fillRect(0, 0, buffer.width, buffer.height);

    features.forEach(feature => {
      bufferContext.fillStyle = 'white';
      bufferContext.fillRect(feature.x - 1, feature.y - 1, feature.width + 2, feature.height + 2);
    });

    const initialNormalImage = await this.getImageWithURL(buffer.toDataURL());

    if (!this.normalConverter) {
      this.normalConverter = new HeightToNormal();
    }

    this.normalConverter.apply(initialNormalImage, {
      blurSharp: 0,
      level: 1,
      strength: 1,
      invertedRed: true,
      invertedGreen: false,
      invertedSource: false,
      type: NORMA_MAP_TYPES.SOBEL,
    });

    const i = await this.getImageWithURL(this.normalConverter.canvas.toDataURL());

    bufferContext.globalCompositeOperation = 'multiply'
    bufferContext.drawImage(i, 0, 0);

    const snap = await this.getImageWithURL(buffer.toDataURL());

    bufferContext.globalCompositeOperation = 'source-over';
    bufferContext.fillStyle = 'white'
    bufferContext.fillRect(0, 0, buffer.width, buffer.height);

    features.forEach(feature => {
      bufferContext.fillStyle = 'white';
      bufferContext.clearRect(feature.x - 1, feature.y - 1, feature.width + 2, feature.height + 2);
    });

    bufferContext.globalCompositeOperation = 'xor';
    bufferContext.drawImage(snap, 0, 0);

    const outputBuffer = this.getCompositedTexture(buffer, existingTexture);

    const normalTexture = await (new THREE.TextureLoader()).loadAsync(outputBuffer.toDataURL());
    normalTexture.anisotropy = this.renderer?.capabilities.getMaxAnisotropy() ?? 1;
    normalTexture.dispose();
    return normalTexture;
  }

  async getDisplacementTexture(features: TextureFeature[], existingTexture: HTMLCanvasElement) {
    const buffer = document.createElement('canvas');
    buffer.width = existingTexture.width;
    buffer.height = existingTexture.height;

    const bufferContext = buffer.getContext('2d');
    assert(bufferContext, 'No context for buffer');

    bufferContext.drawImage(existingTexture, 0, 0);

    features.forEach(feature => {
      bufferContext.fillStyle = 'white';
      bufferContext.fillRect(feature.x - 1, feature.y - 1, feature.width + 2, feature.height + 2);
    });

    const texture = await (new THREE.TextureLoader()).loadAsync(buffer.toDataURL());
    texture.anisotropy = this.renderer?.capabilities.getMaxAnisotropy() ?? 1;
    texture.dispose();
    return texture;
  }

  async getAOTexture(features: TextureFeature[], existingTexture: HTMLCanvasElement) {
    const buffer = document.createElement('canvas');
    buffer.width = existingTexture.width;
    buffer.height = existingTexture.height;

    const bufferContext = buffer.getContext('2d');
    assert(bufferContext, 'No context for buffer');

    bufferContext.drawImage(existingTexture, 0, 0);

    features.forEach(feature => {
      bufferContext.fillStyle = 'black';
      bufferContext.fillRect(feature.x, feature.y, feature.width, feature.height);
    });

    const texture = await (new THREE.TextureLoader()).loadAsync(buffer.toDataURL());
    texture.anisotropy = this.renderer?.capabilities.getMaxAnisotropy() ?? 1;
    texture.dispose();
    return texture;
  }

  getCompositedTexture(newTexture: HTMLCanvasElement, existingTexture?: HTMLImageElement | HTMLCanvasElement, globalCompositeOperation: GlobalCompositeOperation = 'source-over') {
    const outputBuffer = document.createElement('canvas');
    outputBuffer.width = existingTexture?.width ?? newTexture.width;
    outputBuffer.height = existingTexture?.height ?? newTexture.height;

    const outputBufferContext = outputBuffer.getContext('2d');
    assert(outputBufferContext, 'No context for buffer');

    if (existingTexture) {
      outputBufferContext.drawImage(existingTexture, 0, 0);
      outputBufferContext.globalCompositeOperation = globalCompositeOperation;
    }

    outputBufferContext.drawImage(newTexture, outputBuffer.width / 2 - newTexture.width / 2, outputBuffer.height / 2 - newTexture.height / 2);
    return outputBuffer;
  }

  getTextureWidthForWidth(width: number) {
    const map: Record<string, { width: number, height: number, fontScript: number, fontBlock: number }> = {
      '1.5': { width: 150, height: 7446, fontScript: NaN, fontBlock: NaN },
      '2': { width: 200, height: 7446, fontScript: 1.4, fontBlock: 1.2 },
      '2.5': { width: 250, height: 7446, fontScript: 1.4, fontBlock: 1.2 },
      '3': { width: 300, height: 7478, fontScript: 1.6, fontBlock: 1.4 },
      '3.5': { width: 350, height: 7450, fontScript: 1.6, fontBlock: 1.4 },
      '4': { width: 400, height: 7446, fontScript: 1.8, fontBlock: 1.6 },
      '4.5': { width: 450, height: 7446, fontScript: 1.8, fontBlock: 1.6 },
      '5': { width: 500, height: 7446, fontScript: 2, fontBlock: 1.8 },
      '5.5': { width: 550, height: 7446, fontScript: 2, fontBlock: 1.8 },
      '6': { width: 600, height: 7446, fontScript: 2.1, fontBlock: 1.9 },
      '6.5': { width: 650, height: 7446, fontScript: 2.1, fontBlock: 1.9 },
      '7': { width: 700, height: 7446, fontScript: 2.2, fontBlock: 2 },
      '7.5': { width: 750, height: 7446, fontScript: 2.2, fontBlock: 2 },
      '8': { width: 800, height: 7446, fontScript: 2.3, fontBlock: 2.1 },
      '8.5': { width: 850, height: 7446, fontScript: 2.3, fontBlock: 2.1 },
      '9': { width: 900, height: 7446, fontScript: 2.4, fontBlock: 2.2 }
    };
    return map[width.toString()];
  }

  getStoneCountForArrangement(arrangement: string, maxStoneCount: number) {
    const lowerCaseArrangement = arrangement.toLowerCase();
    if (lowerCaseArrangement.includes('eternity')) {
      return maxStoneCount;
    }

    if (lowerCaseArrangement.includes('half')) {
      return Math.ceil(maxStoneCount / 2);
    }

    if (lowerCaseArrangement.includes('third')) {
      return Math.ceil(maxStoneCount / 3);
    }

    return 1;
  }

  async getImageWithURL(url: string) {
    const image = document.createElement('img');
    image.crossOrigin = 'anonymous';
    await new Promise<void>((res) => {
      image.onload = () => {
        res();
      }
      image.src = url;
    });
    return image;
  }

  /**
   * How much closer the camera sits when the cards are stacked. They occupy a ~138px strip on the
   * right instead of ringing the model, so the ring has nearly the whole canvas to itself and the
   * default distance leaves it looking small (ADO #8909). Below 1 = closer = more zoomed in.
   */
  stackedZoomFactor = 0.6;

  setOrbitControlsZoomOptions(windowWidth: number) {
    assert(this.farE, 'farE was null')
    assert(this.orbitControls, 'orbitcontrols was null');
    if (windowWidth >= this.noAnnotationLowerBound) {
      const baseDistance = this.farE * 1.8 * (this.cardMode() === 'Stacked' ? this.stackedZoomFactor : 1);
      if ((this.windowWidth() / this.windowHeight()) < 0.85) {
        this.orbitControls.maxDistance = baseDistance * this.windowHeight() * 1.2 / this.windowWidth();
        this.orbitControls.minDistance = baseDistance * this.windowHeight() * 1.2 / this.windowWidth();
        if (this.camera && this.camera.far < this.orbitControls.maxDistance * 2) {
          this.camera.far = this.orbitControls.maxDistance * 5;
          this.camera.updateProjectionMatrix();
        }
      } else {
        this.orbitControls.maxDistance = baseDistance;
        this.orbitControls.minDistance = baseDistance;
      }
      this.orbitControls.enableZoom = false;
    } else {
      this.orbitControls.maxDistance = this.farE * 1.2
      this.orbitControls.minDistance = this.farE * 1.2;
      this.orbitControls.enableZoom = true;
    }
    this.orbitControls.update();
  }

  async doInitialSetup(imgCanvasElement: HTMLCanvasElement) {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera( 50, imgCanvasElement.clientWidth / imgCanvasElement.clientHeight, 0.1, 2000 );
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, canvas: imgCanvasElement, preserveDrawingBuffer: true });
    this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
    this.camera.up.set(0, 0, 1);
    this.orbitControls.enablePan = false;
    // this.scene.background = new THREE.Color('#f0f0f0');

    this.renderer.setSize( imgCanvasElement.clientWidth, imgCanvasElement.clientHeight );

    this.renderer.toneMapping = THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = 6;

    // this.pathTracer = new WebGLPathTracer(this.renderer);
    // this.pathTracer.setScene(this.scene, this.camera);
    // // this.pathTracer.setBVHWorker(new ParallelMeshBVHWorker());

    // this.pathTracer.bounces = 30;
    // this.pathTracer.minSamples = 10;

    // this.effectComposer = new EffectComposer(this.renderer);
    // this.effectComposer.renderTarget1.samples = 1000;
    // this.effectComposer.renderTarget2.samples = 1000;
    // this.effectComposer.setSize(imgCanvasElement.clientWidth, imgCanvasElement.clientHeight);

    // const renderPass = new RenderPass(this.scene, this.camera);
    // // renderPass.clear = false;
    // // renderPass.clearAlpha = 0;
    // this.effectComposer.addPass(renderPass);
    // // this.effectComposer.addPass(new UnrealBloomPass(new THREE.Vector2(imgCanvasElement.clientWidth * this.renderer.getPixelRatio(), imgCanvasElement.clientHeight * this.renderer.getPixelRatio()), 0.4, 0.7, 0.85));

    // this.effectComposer.addPass(new OutputPass());
    // this.effectComposer.addPass(new SMAAPass(imgCanvasElement.clientWidth, imgCanvasElement.clientHeight));

    const exrLoader = new EXRLoader();
    await new Promise<void>(res => exrLoader.load(getAssetURL('compressed-environment.1741040677954.exr'), (texture) => {
      assert(this.scene, 'Scene was null');
      texture.mapping = THREE.EquirectangularReflectionMapping;
      texture.needsUpdate = true;
      this.scene.environment = texture;
      this.scene.environmentRotation.z = -100 * Math.PI / 180;
      this.scene.environmentRotation.y = -35 * Math.PI / 180;
      this.scene.environmentRotation.x = 90 * Math.PI / 180;
      texture.dispose();
      res();
    }));

    // this.scene.background = new THREE.Color(0.8, 0.8, 0.8);
    // this.scene.background = new THREE.Color(1,1,1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    THREE.ColorManagement.enabled = true;

    this.cssRenderer = new CSS2DRenderer();
    this.cssRenderer.setSize(imgCanvasElement.clientWidth, imgCanvasElement.clientHeight);
    this.cssRenderer.domElement.style.position = 'absolute';
    this.cssRenderer.domElement.style.top = '0px';
    this.cssRenderer.domElement.style.pointerEvents = 'none';
    imgCanvasElement.parentElement?.appendChild(this.cssRenderer.domElement);
  }

  async createModel(configuredItem: ConfiguredItem, itemAssets: ItemAsset[], gltfFileOverride: string | undefined = undefined) {
    const itemAssetsToUse = itemAssets.slice();
    const modelAssetIndex = itemAssetsToUse.findIndex(ia => ia.location === null);
    assertTrue(modelAssetIndex >= 0, 'No index for model asset found');
    const [modelAsset] = itemAssetsToUse.splice(modelAssetIndex, 1);
    assert(modelAsset, 'No model asset found');

    const model: any = await new Promise((res, rej) => this.gltfLoader.load(gltfFileOverride ?? getAssetURL(`${modelAsset.fileName}`), res, undefined, rej));
    model.scene.children[0].scale.set(1,1,1);
    const modelSceneBoundingBox = new THREE.Box3();
    modelSceneBoundingBox.setFromObject(model.scene);
    const modelSceneSize = new THREE.Vector3();
    modelSceneBoundingBox.getSize(modelSceneSize);
    model.scene.scale.set((23.8 / modelSceneSize.x), 1, (23.8 / modelSceneSize.x));

    assert(configuredItem.primaryMaterial, 'No primary material');
    assert(configuredItem.design, 'No design');

    const modelMeshes: any[] = [];
    model.scene.traverse((descendant: any) => {
      if (descendant.isMesh) {
        const material = new descendant.material.constructor();
        material.copy(descendant.material);
        descendant.material.dispose();
        descendant.material = material;
        modelMeshes.push(descendant);
      }
    });

    model.scene.updateWorldMatrix(true, true);
    modelMeshes.forEach((mesh) => {
      const actualModelSize = new THREE.Vector3();
      const actualBoundingBox = new THREE.Box3();
      actualBoundingBox.setFromObject(mesh)
      actualBoundingBox.getSize(actualModelSize);
      mesh.geometry.computeBoundingBox();
      const targetSize = new THREE.Vector3();
      mesh.geometry.boundingBox.getSize(targetSize);
      const targetMin = Math.min(targetSize.x, targetSize.y, targetSize.z);
      const actualMin = Math.min(actualModelSize.x, actualModelSize.y, actualModelSize.z);
      const scaleFactor = targetMin / actualMin;
      mesh.scale.multiplyScalar(scaleFactor);
      mesh.updateWorldMatrix(true, true);
    });

    await this.addTextures(modelMeshes, itemAssetsToUse);

    // C1/C2 (b): addTextures above overlaps the inside-texture prefetch (in flight
    // since loadModel; inside-texture READS there use getImageWithURL directly,
    // not the cache, so overlap is safe). The engraving step is the only cache
    // reader, so join the prefetch immediately before it; per-texture logic and
    // the addTextures signature are unchanged.
    if (configuredItem.engravingText && configuredItem.engravingType) {
      await this.pendingInsideTextureFill;
      await this.addEngravingTextures(modelMeshes, itemAssetsToUse, configuredItem);
    }

    this.setMaterialsColor(modelMeshes, configuredItem);

    return model;
  }

  async addTextures(modelMeshes: any[], itemAssets: ItemAsset[]) {
    const indexMap = this.getTextureLocationIndexMap(modelMeshes.length);

    const textureLoader = new THREE.TextureLoader();

    const maxAnisotropy = this.renderer?.capabilities.getMaxAnisotropy() ?? 1;

    await Promise.all(modelMeshes.map(async (mesh, i) => {
      const location = indexMap[i];
      const texturesForThisLocation = itemAssets.filter(ia => ia.location === location);

      mesh.material.roughness = 1;
      mesh.material.metalness = 1;
      mesh.material.displacementScale = -1;
      mesh.material.normalMapType = THREE.TangentSpaceNormalMap;

      await Promise.all(texturesForThisLocation.map(async (texture) => {
        const textureFile = await textureLoader.loadAsync(getAssetURL(texture.fileName));
        // textureFile.generateMipmaps = false;
        const materialTextureKey = [ItemAssetLocationToThreeJSKeyMap[texture.type]].flat();
        materialTextureKey.forEach(key => {
          mesh.material[key] = textureFile;
          mesh.material[key].anisotropy = maxAnisotropy;
          mesh.material[key].needsUpdate = true;
        });
      }));

      mesh.material.needsUpdate = true;
    }));
  }

  getTextureLocationIndexMap(modelMeshCount: number) {
    const indexMap = ['Outside'];
    if (modelMeshCount === 3) {
      indexMap.push('Edge');
    }
    indexMap.push('Inside');
    return indexMap;
  }

  setMaterialsColor(modelMeshes: any, configuredItem: ConfiguredItem) {
    const edgeIndex = modelMeshes.length === 2 ? null : 1;
    const outsideIndex = 0;
    const insideIndex = modelMeshes.length === 2 ? 1 : 2;

    assert(configuredItem.primaryMaterial, 'No primary material');

    modelMeshes[insideIndex].material.color.setHex(+configuredItem.primaryMaterial.color);
    if (edgeIndex) {
      modelMeshes[edgeIndex].material.color.setHex(+configuredItem.primaryMaterial.color);
    }

    if (configuredItem.secondaryMaterial) {
      modelMeshes[outsideIndex].material.color.setHex(+configuredItem.secondaryMaterial.color);
    }
  }

  getMapWidthPrefix(width: number) {
    if (width <= 3) {
      return '030';
    }

    if (width <= 5) {
      return '050';
    }

    return '070';
  }

  addShadows(boundingBoxes: THREE.Box3[], originalModelSizeVector: THREE.Vector3, modelGroup: THREE.Group, blurPlane: THREE.Mesh, shadowCam: THREE.Camera, renderTarget: THREE.WebGLRenderTarget, shadowLift = 0) {
    const shadows = new THREE.Group();
    shadows.name = 'shadows';
    shadows.rotation.set(0, Math.PI + Math.PI / 2, Math.PI / 2);
    modelGroup.add(shadows);

    const maxZ = Math.max(...boundingBoxes.map(b => b.max.z));

    const plane = new THREE.Mesh(blurPlane.geometry, new THREE.MeshBasicMaterial({ map: renderTarget.texture, opacity: 0.2, transparent: true, depthWrite: false }));
    plane.name = 'plane1';
    plane.renderOrder = 1;
    plane.position.y = maxZ - shadowLift;
    shadows.add(plane);

    const plane2SizeVector = new THREE.Vector3();
    plane2SizeVector.copy(originalModelSizeVector);
    plane2SizeVector.multiplyScalar(1.5);
    plane2SizeVector.y *= 1.1;
    plane2SizeVector.x *= 1.2;
    const plane2 = new THREE.Mesh(new THREE.PlaneGeometry(plane2SizeVector.x, plane2SizeVector.y).rotateX(Math.PI / 2), new THREE.MeshBasicMaterial({ map: renderTarget.texture, opacity: 0.3, transparent: true, depthWrite: false }));
    plane2.name = 'plane2';
    plane2.renderOrder = 2;
    plane2.position.y = maxZ - shadowLift;
    shadows.add(plane2);

    plane.scale.set(1.3, -1, 1.3);
    plane.material.side = THREE.BackSide;

    blurPlane.visible = false;
    shadows.add(blurPlane);

    shadowCam.rotation.x = Math.PI / 2;
    shadows.add(shadowCam);
  }

  addOverlay(boundingBox: THREE.Box3, overlayButtons: readonly ElementRef<any>[]) {
    this.overlayGroup = new THREE.Group();
    this.overlayGroup.name = 'overlay'

    this.positionIndicatorBoundingSphere = (new THREE.Box3()).setFromObject(this.model.scene).getBoundingSphere(new THREE.Sphere());

    const positionIndicator = this.positionIndicator();
    if (positionIndicator) {
      this.positionIndicatorObject = new CSS2DObject(positionIndicator.nativeElement);
      this.positionIndicatorObject.center.set(0, 0);
      this.overlayGroup?.add(this.positionIndicatorObject);
    }

    assert(this.modelGroup, 'Model group was null');
    if (this.shouldShowAnnotations()) {
      this.modelGroup.add(this.overlayGroup);
    }
  }

  threeAnimate() {
    assert(this.renderer, 'Renderer was null');
    assert(this.scene, 'Scene was null');
    assert(this.camera, 'Camera was null');

    this.scene.overrideMaterial = this.depthMaterial ?? null;

    const initialClearAlpha = this.renderer.getClearAlpha();
    this.renderer.setClearAlpha(0);

    this.renderer.setRenderTarget(this.renderTarget ?? null);

    if (this.shadowCamera) {
      this.renderer.render(this.scene, this.shadowCamera);
    }

    this.scene.overrideMaterial = null;

    assert(this.blurPlane, 'blurplane was null');
    assert(this.renderTarget, 'renderTarget was null');
    assert(this.blurRenderTarget, 'blurRenderTarget was null');
    assert(this.shadowCamera, 'shadowCamera was null');
    assert(this.horizontalBlurMaterial, 'horizontalBlurMaterial was null');
    assert(this.verticalBlurMaterial, 'verticalBlurMaterial was null');
    const blurFactor = 2;
    this.blurShadow(blurFactor * 4, this.renderer, this.blurPlane, this.shadowCamera, this.renderTarget, this.blurRenderTarget, this.horizontalBlurMaterial, this.verticalBlurMaterial);
    this.blurShadow(blurFactor * 8.5, this.renderer, this.blurPlane, this.shadowCamera, this.renderTarget, this.blurRenderTarget, this.horizontalBlurMaterial, this.verticalBlurMaterial);

    this.renderer.setRenderTarget(null);
    this.renderer.setClearAlpha(initialClearAlpha);

    this.trackballControls?.update();

    this.renderer.render(this.scene, this.camera);
    // this.effectComposer?.render();

    // this.pathTracer?.updateCamera()
    // this.pathTracer?.renderSample();

    if (this.compareCSSRenderer) {
      this.compareCSSRenderer.render(this.scene, this.camera);
    }

    if (this.shouldShowAnnotations() && !this.isSharing() && this.positionIndicatorBoundingSphere && this.cardMode() !== 'None' && this.controlOverlay()) {
      this.updateAnnotationCoords();
    }
  }

  blurShadow(factor: number, renderer: THREE.WebGLRenderer, blurPlane: THREE.Mesh, shadowCamera: THREE.Camera, renderTarget: THREE.WebGLRenderTarget, blurRenderTarget: THREE.WebGLRenderTarget, horizontalBlurMaterial: THREE.ShaderMaterial, verticalBlurMaterial: THREE.ShaderMaterial) {
    blurPlane.visible = true;

    (blurPlane as any).material = horizontalBlurMaterial;
    (blurPlane.material as any).uniforms.tDiffuse.value = renderTarget.texture;
    horizontalBlurMaterial.uniforms['h'].value = factor * 1 / (256 * 5);

    renderer.setRenderTarget( blurRenderTarget );
    renderer.render(blurPlane, shadowCamera );

    (blurPlane.material as any) = verticalBlurMaterial;
    (blurPlane.material as any).uniforms['tDiffuse'].value = blurRenderTarget.texture;
    verticalBlurMaterial['uniforms']['v'].value = factor * 1 / (256 * 5);

    renderer.setRenderTarget( renderTarget );
    renderer.render(blurPlane, shadowCamera );

    blurPlane.visible = false;
  }

  ngAfterViewInit() {
    this.windowWidth.set(window.innerWidth);
    this.windowHeight.set(window.innerHeight);
  }



  mainImageLoaded() {
    this.model.scene.rotation.set(0, 15 * Math.PI / 180, 0);
    if (this.shouldDoInitialCameraReposition) {
      const matchingAngle = this.angles[this.navigationService.activeView()?.title];
      if (matchingAngle) {
        setTimeout(() => {
          this.setCameraPosition(matchingAngle.polar, matchingAngle.azimuthal);
        }, 500);
      }
    }
  }

  @HostListener('window:resize', [])
  windowResize() {
    this.resizeCanvas();

    const imgCanvasElement = this.imgCanvas()?.nativeElement;
    if (imgCanvasElement) {
      if (this.camera) {
        this.camera.aspect = (imgCanvasElement.parentElement?.clientWidth ?? 0) / (imgCanvasElement.parentElement?.clientHeight ?? 0);
        this.camera.updateProjectionMatrix();
      }

      if (this.renderer) {
        this.renderer.setSize((imgCanvasElement.parentElement?.clientWidth ?? 0), (imgCanvasElement.parentElement?.clientHeight ?? 0));
      }

      if (this.cssRenderer) {
        this.cssRenderer.setSize((imgCanvasElement.parentElement?.clientWidth ?? 0), (imgCanvasElement.parentElement?.clientHeight ?? 0));
      }

      if (this.compareCSSRenderer) {
        this.compareCSSRenderer.setSize((imgCanvasElement.parentElement?.clientWidth ?? 0), (imgCanvasElement.parentElement?.clientHeight ?? 0));
        if (this.windowWidth() <= this.noAnnotationLowerBound) {
          this.compareCSSRenderer.domElement.style.transform = '';
        } else {
          this.compareCSSRenderer.domElement.style.transform = 'translateY(5px)';
        }
        this.compareCSSRenderer.domElement.style.position = 'absolute';
        this.compareCSSRenderer.domElement.style.top = '0px';
        this.compareCSSRenderer.domElement.style.height = '0px';
        this.compareCSSRenderer.domElement.style.width = '0px';
        this.compareCSSRenderer.domElement.style.overflow = 'unset';
      }
    }
  }

  /** window.innerHeight at or below which cards fold VERTICALLY (wider and shorter). */
  verticalFoldViewportHeight = readBreakpoint('--breakpoint-vertical-fold-height', 630);

  /**
   * window.innerHeight at or below which the cards stop being placed around the ring altogether and
   * become a scrollable vertical list anchored to the right, beside the attribute panel. Below this
   * there is not enough height to arrange anything around the ring, so cards return to Normal
   * sizing and stack instead (ADO #8909).
   */
  stackedViewportHeight = readBreakpoint('--breakpoint-stacked-height', 430);

  /**
   * Below this width the annotations are hidden while vertically folded. Sooner than the normal
   * 770 cutoff because vertically folded cards are wider.
   */
  noAnnotationLowerBoundWhenShort = readBreakpoint('--breakpoint-mobile-when-short-width', 890);

  updateCardMode() {
    // Exhaustive and mutually exclusive -- every (width, height) maps to exactly one mode, with the
    // boundaries expressed as <= / > on the SAME constant so there is no dead spot between
    // breakpoints (ADO #8909).
    //
    //   innerHeight <= 430  stacked: width <  770 -> None            width >= 770 -> Stacked
    //   innerHeight <= 630  short:   width <  890 -> None            width >= 890 -> FoldedVertical
    //   innerHeight >  630  tall:   width <= 770 -> None            (original breakpoint)
    //                               width >  770 -> FoldedHorizontal if cards would overflow
    //                                               Normal otherwise
    if (this.windowHeight() <= this.stackedViewportHeight) {
      // Stacked cards are back to Normal width in a ~138px column, so they need far less room than
      // the vertically folded ones -- the plain 770 cutoff applies, not the wider 890 (ADO #8909).
      this.cardMode.set(this.windowWidth() < this.noAnnotationLowerBound ? 'None' : 'Stacked');
      return;
    }

    if (this.windowHeight() <= this.verticalFoldViewportHeight) {
      // 890 applies ONLY in this band: vertically folded cards are the wide ones.
      this.cardMode.set(this.windowWidth() < this.noAnnotationLowerBoundWhenShort ? 'None' : 'FoldedVertical');
      return;
    }

    if (this.windowWidth() < this.noAnnotationLowerBound) {
      this.cardMode.set('None');
      return;
    }

    // willNormalControlOverflow measures the NORMAL card box regardless of the mode we are in, so
    // this cannot feed back on itself and oscillate.
    this.cardMode.set(this.controls.some(control => this.willNormalControlOverflow(control))
      ? 'FoldedHorizontal'
      : 'Normal');
  }

  resizeCanvas() {
    this.windowWidth.set(window.innerWidth);
    this.windowHeight.set(window.innerHeight);
    this.shouldShowAnnotations.set(window.innerWidth >= this.noAnnotationLowerBound && !this.isComparing());
    // Re-evaluate the card mode on every resize. updateCardMode's only other caller is
    // updateAnnotationCoords, which is gated on cardMode !== 'None' -- so once the cards went to
    // 'None' nothing ever reconsidered it and they never came back (ADO #8909). Annotations should
    // only vanish for the mobile layout, and they must return when there is room again.
    this.updateCardMode();

    this.changeDetectorRef.detectChanges();
  }

  getCircleControlStyle(control: OverlayControl) {
    return { left: `${control.point.x - 13}px`, top: `${control.point.y - 13}px` };
  }

  getConfiguredItemValue(key: string) {
    if (key === 'gemstones') {
      const arrangementWithSetting = this.stoneArrangementsWithSettingsService.values().find(aws => aws.stoneArrangementID === this.selectedAttributeService.configuredItem().gemstoneArrangement?.id && aws.stoneSettingID === this.selectedAttributeService.configuredItem().gemstoneSetting?.id);
      const typeWithShape = this.stoneTypesWithShapesService.values().find(tws => tws.stoneTypeID === this.selectedAttributeService.configuredItem().gemstoneType?.id && this.selectedAttributeService.configuredItem().gemstoneShape?.id === this.selectedAttributeService.configuredItem().gemstoneShape?.id);

      if (this.selectedAttributeService.configuredItem().gemstoneType) {
        const countWithSize = `${this.stoneCount()} x ${this.selectedAttributeService.configuredItem().gemstoneSize?.displayName}`;

        // Three values, unchanged from the original. Expanded, the template puts a <br> between
        // them so the card reads exactly as it always has; collapsed, it drops the breaks and they
        // sit side by side as columns on a single line (ADO #8909). Folding is a layout change,
        // not a content change -- no information is dropped either way.
        return [countWithSize, `${typeWithShape?.displayName}`, `${arrangementWithSetting?.displayName}`];
      }

      return 'Click to Add';
    }

    if (key === 'engraving') {
      const engravingText = this.selectedAttributeService.configuredItem().engravingText;
      return (this.selectedAttributeService.configuredItem().engravingType && engravingText ? engravingSpecialChars.reduce((str, s) => str.replace(new RegExp(`${s.haloCharUnicode}`, 'g'), s.char), engravingText) : 'None');
    }

    const value = (this.selectedAttributeService.configuredItem() as any)[key].displayName;
    return !!value ? value : 'None';
  }

  getValueLength(value: string | string[]) {
    if (typeof value === 'string') {
      return value.length;
    }
    return value.join('').length;
  }

  async overlayButtonClick(title: string) {

    this.triggerGAEvent('click', 'overlay_button', title);

    if (title === 'Gemstones' && !this.selectedAttributes().gemstoneType) {
      const arrangementWithSetting = this.stoneArrangementsWithSettingsService.values()[0];
      const typeWithShape = this.stoneTypesWithShapesService.values()[0];

      const matchingArrangement = this.stoneArrangementsService.values().find(arrangement => arrangement.id === arrangementWithSetting.stoneArrangementID);
      assert(matchingArrangement, 'No matching arrangement');

      const matchingSetting = this.stoneSettingsService.values().find(setting => setting.id === arrangementWithSetting.stoneSettingID);
      assert(matchingSetting, 'No matching setting');

      const matchingType = this.stoneTypesService.values().find(type => type.id === typeWithShape.stoneTypeID);
      assert(matchingType, 'No matching arrangement');

      const matchingShape = this.stoneShapesService.values().find(shape => shape.id === typeWithShape.stoneShapeID);
      assert(matchingShape, 'No matching setting');

      const size = this.stoneSizesService.values()[0];

      const newConfiguredItemToValidate = Object.assign({}, this.selectedAttributes(), { gemstoneType: matchingType, gemstoneSetting: matchingSetting, gemstoneSize: size, gemstoneArrangement: matchingArrangement, gemstoneShape: matchingShape });

      this.mainImageService.shouldMakeGemstoneSectionActive = true;
      if (!await this.rulesService.getIsValid(newConfiguredItemToValidate, this.checkoutService.isRetailerOnly() ? this.checkoutService.retailerIframeID : null)) {
        this.loadingService.load('MainImage::SetClosestStoneType');
        await this.selectedAttributeService.setClosestByStoneTypeWithShape(typeWithShape, newConfiguredItemToValidate, true);
        this.loadingService.done('MainImage::SetClosestStoneType');
      } else {
        this.selectedAttributeService.mutate(c => {
          c.gemstoneType = matchingType;
          c.gemstoneSetting = matchingSetting;
          c.gemstoneSize = size;
          c.gemstoneArrangement = matchingArrangement;
          c.gemstoneShape = matchingShape;
        }, 'Gemstone');
      }
    } else if (title === 'Engraving') {
      await this.makeSectionActive('Engraving')
    } else {
      await this.makeSectionActive(title);
    }

  }

  async makeSectionActive(title: string) {
    this.didInteractWithImage.set(true);
    const viewIndex = this.navigationService.views().findIndex(v => v.title === title);
    if (this.navigationService.activeViewIndex() !== viewIndex) {
      this.navigationService.navigateTo(viewIndex);
    } else {
      this.positionCameraForActiveView();
    }
  }

  getStyle(key: string) {
    if (key === 'engraving' && this.selectedAttributes().engravingType && this.selectedAttributes().engravingText) {
      const baseStyle = {
        width: '100%',
        overflow: 'hidden',
        'text-wrap': 'nowrap',
        height: '16px',
        'text-overflow': 'ellipsis',
        display: 'block',
        flex: 'none',
        'padding-top': '4px'
      };

      const style = this.selectedAttributes().engravingType?.displayName === 'Block' ? {'font-family': '"engraving_block"', 'font-size': '0.95rem'} : {'line-height': '1ch', 'font-family': '"engraving_script"', 'font-size': '1.1rem'};
      return Object.assign({}, baseStyle, style);
    }

    return null;
  }

  @HostListener('animationend', ['$event.target'])
  viewContainerAnimationEnd(target: EventTarget | null) {
    if (target) {
      const castTarget = target as HTMLElement;
      if (castTarget.classList.contains('InnerCircle')) {
        if (castTarget.classList.contains('AnimateOut')) {
          if (this.controlToAnimateOut) {
            this.controlToAnimateOut.isActive = false;
            this.controlToAnimateOut = null;
          }
        }

        if (castTarget.classList.contains('AnimateIn')) {
          this.controlToAnimateIn = null;
        }
      }
    }
  }

  overlayButtonHover(displayName: string | null) {
    this.shouldClearHover = !displayName;

    this.controlLabelToStyleHovered.set(displayName);

    this.didInteractWithImage.set(true);
  }

  setCameraPosition(polar: number, azimuthal: number, completion?: () => void) {
    const orbitControls = this.orbitControls;
    assert(orbitControls, 'No orbit controls');

    const camera = this.camera;
    assert(camera, 'No camera');

    this.shouldCancelSectionAnimation = false;
    requestAnimationFrame((ts) => this.animateSetCamera(ts, document.timeline.currentTime as any, orbitControls.getPolarAngle(), orbitControls.getAzimuthalAngle(), polar, azimuthal, () => {
      this.orbitControls?.setPolarAngle(polar);
      this.orbitControls?.setAzimuthalAngle(azimuthal);
      this.didUserPan = false;
      completion?.call(this);
    }));
  }

  animateSetCamera(ts: number, start: number, startPolar: number, startAzimuthal: number, endPolar: number, endAzimuthal: number, completion?: () => void) {
    if (this.shouldCancelSectionAnimation) {
      return;
    }

    const duration = 700;
    const progress = (ts - start) / duration;

    if (progress < 1 && endPolar !== startPolar && endAzimuthal !== startAzimuthal) {
      this.orbitControls?.setPolarAngle(startPolar);
      this.orbitControls?.setAzimuthalAngle(startAzimuthal);
      requestAnimationFrame((t) => this.animateSetCamera(t, start, startPolar + (endPolar - startPolar) * progress, startAzimuthal + (endAzimuthal - startAzimuthal) * progress, endPolar, endAzimuthal, completion));
    } else {
        completion?.call(this);
    }
  }

  getEdgeMaterialName() {
    const edgeMaterialView = this.navigationService.views().find(v => v.title === 'Edge Material');
    assert(edgeMaterialView, 'No edge material view');
    return edgeMaterialView.displayTitle ?? edgeMaterialView.title;
  }

  getControlStyle(control: OverlayControl) {
    const style: any = {};

    // != null, not truthiness: a coordinate of exactly 0 is a valid position (the clamp can put a
    // card flush against the top or left edge), and treating it as absent hid the card (ADO #8909).
    // Stacked lays the cards out as a flow list, so they must NOT be absolutely positioned -- no
    // left/top, and no hiding either (ADO #8909).
    if (this.cardMode() === 'Stacked') {
      return style;
    }

    if (control.coordinates?.x != null && control.coordinates?.y != null && this.cardMode() !== 'None') {
      const { x, y } = this.clampControlToCanvas(control);
      Object.assign(style, { left: `${x}px`, top: `${y}px` });
    }

    if (Object.keys(style).length < 1) {
      Object.assign(style, { visibility: 'hidden', 'pointer-events': 'none' });
    }

    return style;
  }

  /**
   * Keeps an annotation inside the previewer instead of letting it hang off the edge (ADO #8909).
   *
   * Overflow was already being detected in {@link willNormalControlOverflow}, but the only response
   * was to switch card mode -- which does not reposition anything, so on a short viewport the card
   * still rendered below the canvas and was clipped by the bottom bar. Clamping here, where
   * coordinates become a position, fixes it for every control rather than just the tallest one.
   */
  /**
   * The annotation angle to draw this control at.
   *
   * Cards sit on a circle at +/-15 and +/-70 degrees either side of the 90/270 axes. design,
   * secondaryMaterial, width and engraving are the +/-70 ring, and the large gap when cards
   * collapse is the one straddling the horizontal axis -- between design (160) and engraving (200)
   * on the left, and between secondaryMaterial (20) and width (340) on the right. Closing it means
   * rotating that ring 10 degrees AWAY from its 90/270 axis, i.e. toward the 180/0 midline
   * (ADO #8909). Rotating toward the axis moves them apart, which was the first attempt and was
   * backwards. The inner +/-15 ring is left alone.
   */
  private getControlAngle(control: OverlayControl) {
    if (this.cardMode() !== 'FoldedVertical') {
      return control.label;
    }

    const axis = control.label >= 180 ? 270 : 90;
    const offset = control.label - axis;

    if (Math.abs(offset) < 70) {
      return control.label;
    }

    return axis + Math.sign(offset) * (Math.abs(offset) + this.collapsedOuterPullDegrees);
  }

  /** How far the outer ring of cards rotates toward the horizontal midline when collapsed. */
  collapsedOuterPullDegrees = 10;

  /**
   * Height to keep clear below a card for its Remove button, which getRemoveButtonStyle places at
   * `card.top + card.height`. Without reserving it, clamping the card flush to the canvas bottom
   * pushes the button straight off the edge (ADO #8909).
   */
  /** Whether this control currently offers a Remove button at all. */
  private hasRemoveButton(control: OverlayControl) {
    return (control.key === 'gemstones' && !!this.selectedAttributes().gemstoneArrangement)
           || (control.key === 'engraving' && !!this.selectedAttributes().engravingText && !!this.selectedAttributes().engravingType);
  }

  private removeButtonClearance(control: OverlayControl) {
    if (!this.hasRemoveButton(control)) {
      return 0;
    }

    const removeButton = this.removeButtons().find(b => b.nativeElement.name === `${control.key}-button`);
    return removeButton ? removeButton.nativeElement.getBoundingClientRect().height : 0;
  }

  private clampControlToCanvas(control: OverlayControl) {
    const x = control.coordinates?.x ?? 0;
    const y = control.coordinates?.y ?? 0;

    // Gemstones keeps its original positioning. Clamping it inward pushed it over the ring itself,
    // which is worse than any overflow -- the product must never be covered. It is the widest and
    // tallest card, so it is the one clamping displaces furthest. Collapsing it to a single line is
    // what keeps it on the canvas instead (ADO #8909).
    if (control.key === 'gemstones') {
      return { x, y };
    }

    const imgCanvas = this.imgCanvas();
    const cardButton = this.overlayButtons().find(overlayButton => overlayButton.nativeElement.name === control.key);
    if (!imgCanvas || !cardButton) {
      return { x, y };
    }

    const canvas = imgCanvas.nativeElement.getBoundingClientRect();
    const card = cardButton.nativeElement.getBoundingClientRect();

    // Math.max(0, ...) so a card larger than the canvas pins to the top-left rather than going
    // negative, which would clip it off the opposite edge instead.
    const maxX = Math.max(0, canvas.width - card.width);
    const maxY = Math.max(0, canvas.height - card.height - this.removeButtonClearance(control));

    return {
      x: Math.min(Math.max(x, 0), maxX),
      y: Math.min(Math.max(y, 0), maxY),
    };
  }

  updateAnnotationCoords() {
    this.updatePositionIndicatorObject();
    const controlsWithNewCoordinates = this.controls.map(control => Object.assign({}, control, { coordinates: this.calculateControlCoordinates(control) ?? undefined }));

    // if (controlsWithNewCoordinates.some(control => this.willControlOverflow(control))) {
    //   controlsWithNewCoordinates.forEach(control => {
    //     control.coordinates = undefined;

    //     if (control.isActive) {
    //       control.coordinates = this.calculateSingleControlCoordinates(control);
    //       if (this.willControlOverflow(control)) {
    //         control.coordinates = undefined;
    //       }
    //     }
    //   });
    // }
    this.controls.forEach(control => {
      control.coordinates = controlsWithNewCoordinates.find(c => c.key === control.key)?.coordinates;
    });

    this.updateCardMode();
  }

  updatePositionIndicatorObject() {
    assert(this.positionIndicatorBoundingSphere, 'No position indicator bounding sphere');
    assert(this.camera, 'No camera');
    assert(this.scene, 'No scene');
    const v1 = new THREE.Vector3(0, 0, 1);
    const v2 = new THREE.Vector3();
    this.camera.getWorldDirection(v2);
    v1.cross(v2).normalize();
    v2.cross(v1).normalize();
    this.positionIndicatorObject?.position.copy(v1.multiplyScalar(this.positionIndicatorBoundingSphere?.radius));
    this.cssRenderer?.render(this.scene, this.camera);
  }

  calculateControlCoordinates(control: OverlayControl) {
    const controlOverlay = this.controlOverlay();
    assert(controlOverlay, 'No control overlay');

    const positionIndicator = this.positionIndicator();
    const imgCanvas = this.imgCanvas();
    if (positionIndicator && imgCanvas) {
      const { x: circleX, y: circleY } = this.getPointOnAnnotationCirclePath(this.getControlAngle(control));

      const overlayButton = this.overlayButtons().find(b => b.nativeElement.name === control.key);
      if (overlayButton) {
        const originOffset = control.calculateOriginOffset(overlayButton.nativeElement);
        return { x: circleX - originOffset.x, y: circleY - originOffset.y };
      }
    }
    return null;
  }

  calculateSingleControlCoordinates(control: OverlayControl) {
    const cardButton = this.overlayButtons().find(overlayButton => overlayButton.nativeElement.name === control.key);
    assert(cardButton, 'No matching card button for control');
    const coords = this.getPointOnAnnotationCirclePath(90);
    const cardButtonBoundingRect = cardButton.nativeElement.getBoundingClientRect();
    return { x: coords.x - cardButtonBoundingRect.width / 2, y: coords.y - cardButtonBoundingRect.height };
  }

  willNormalControlOverflow(control: OverlayControl) {
    if (control.coordinates) {
      const cardButton = this.overlayButtons().find(overlayButton => overlayButton.nativeElement.name === control.key);
      assert(cardButton, 'No matching card button for control');

      const imgCanvas = this.imgCanvas();
      assert(imgCanvas, 'No img canvas');

      const normalCardWidth = cardButton.nativeElement.style.getPropertyValue('--normal-card-width');
      const parsedNormalWidth = +normalCardWidth.replace(/[A-Za-z]/g, '');
      // Height must come from the NORMAL card too, not from getBoundingClientRect(). The rect is
      // the card as currently rendered, so in Small mode this asked "would the SMALL height
      // overflow?" -- the wrong question. It answered no, mode flipped back to Normal, Normal
      // overflowed, and the mode oscillated: at some viewport heights the cards visibly got
      // BIGGER as the window got shorter (ADO #8909). Making Small shorter amplified it.
      const normalCardHeight = cardButton.nativeElement.style.getPropertyValue('--normal-card-height');
      const parsedNormalHeight = +normalCardHeight.replace(/[A-Za-z]/g, '');

      const canvasRect = imgCanvas.nativeElement.getBoundingClientRect();
      const doesOverflow = control.coordinates.x < 0
                           || control.coordinates.y < 0
                           || control.coordinates.x + parsedNormalWidth > canvasRect.width
                           || control.coordinates.y + parsedNormalHeight > canvasRect.height;
      return doesOverflow;
    }
    return false;
  }

  getPointOnAnnotationCirclePath(theta: number) {
    const positionIndicator = this.positionIndicator();
    const imgCanvas = this.imgCanvas();
    assert(positionIndicator, 'No position indicator');
    assert(imgCanvas, 'No img canvas');
    // The radius used to come from canvas WIDTH alone, so on a wide, short canvas it was far larger
    // than the canvas is tall: cards were placed outside the vertical bounds and bunched along the
    // top and bottom edges, where any size change made neighbours collide. Cap it by height too so
    // the ring of cards fits the canvas shape at any aspect ratio (ADO #8909).
    const canvasRect = imgCanvas.nativeElement.getBoundingClientRect();
    const radiusFromWidth = (canvasRect.width / 2 - positionIndicator.nativeElement.getBoundingClientRect().left) * 0.9;
    const radiusFromHeight = (canvasRect.height / 2) * 0.9;
    const r = Math.min(radiusFromWidth, radiusFromHeight);
    const circleX = imgCanvas.nativeElement.width / 2 + r * Math.cos(theta * Math.PI / 180);
    const circleY = imgCanvas.nativeElement.height / 2 + r * -1 * Math.sin(theta * Math.PI / 180);
    return { x: circleX, y: circleY };
  }

  getSystemChange(controlKey: string) {
    if (controlKey.toLowerCase().includes('stone')) {
      return this.systemChanges().find(change => change.key.toLowerCase().includes('stone'));
    }
    return this.systemChanges().find(change => change.key === controlKey);
  }

  triggerGAEvent(action: string, title: string, value: string) {
    if(this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent(action, 'previewer', title, value);
    }
  }

  cleanUpCompareGroup() {
    this.scene?.children.filter(c => c.name === 'CompareGroup')?.forEach(c => {
      c.traverse((descendant: any) => {
         if (descendant.isMesh) {
            Object.keys(descendant.material).forEach(k => {
              if (!!descendant.material[k]?.dispose && !k.startsWith('_')) {
                descendant.material[k].dispose();
              }
            });

            descendant.material?.dispose();
          }
      });
      if (this.compareCSSRenderer) {
        this.compareCSSRenderer.domElement.innerHTML = '';
      }
      c.removeFromParent();
    });
  }

  setupNormalCamera() {
    assert(this.camera, 'No camera');
    assert(this.scene, 'No scene');
    const boundingBox = new THREE.Box3().setFromObject(this.model.scene, true);
    const fov = this.camera.fov * Math.PI / 180;
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    const z = (Math.abs(maxDim / 4 * Math.tan(fov * 2))) * 0.6;

    const minZ = boundingBox.min.z;
    this.farE = (minZ < 0) ? -minZ + z : z - minZ;

    this.camera.far = this.farE * 3;
    this.camera.updateProjectionMatrix();

    assert(this.orbitControls, 'No orbit controls');
    this.setOrbitControlsZoomOptions(this.windowWidth());
    this.orbitControls.target.set(0, 0, 0);
    this.orbitControls.update();

    this.orbitControls.maxPolarAngle = Infinity;
    this.orbitControls.minPolarAngle = -Infinity;
  }

  positionCameraForActiveView() {
    const title = this.navigationService.activeView()?.title;
    if (this.orbitControls) {
      const matchingAngle = this.angles[title];
      if (matchingAngle) {
        this.setCameraPosition(matchingAngle.polar, matchingAngle.azimuthal);
      } else {
        this.setCameraPosition(0.57, -0.41);
      }
    }
  }

  imageCanvasMouseDown() {
    this.shouldCancelMouseClick = false;
    this.isMouseDown = true;
  }

  imageCanvasMouseMove() {
    if (this.isMouseDown) {
      this.shouldCancelMouseClick = true;
    }
  }

  imageCanvasMouseUp(event: MouseEvent) {
    this.isMouseDown = false;
    if (!this.shouldCancelMouseClick && this.camera && this.scene) {
      if (!this.pointer) {
        this.pointer = new THREE.Vector2();
      }

      this.pointer.x = (event.clientX / (this.imgCanvas()?.nativeElement.getBoundingClientRect().width ?? 0)) * 2 - 1;
      this.pointer.y = -(event.clientY / (this.imgCanvas()?.nativeElement.getBoundingClientRect().height ?? 0)) * 2 + 1;
      this.raycaster.setFromCamera(this.pointer, this.camera);
      const compareGroup = this.scene.children.find(child => child.name === 'CompareGroup');
      if (compareGroup) {
        const closestObject = this.raycaster.intersectObjects(compareGroup.children)[0];
        if (closestObject) {
          let parent = closestObject.object.parent;
          while(parent && parent?.parent?.name !== 'CompareGroup') {
            parent = parent.parent;
          }
          if (parent) {
            this.bookmarksService.select(+parent.name);
          }
        }
      }
    }
  }

  getStyleImageViewButtonStyle(control: OverlayControl) {
    // Stacked lays cards out as a flow list, so this button flows underneath its card instead of
    // being positioned off the card's absolute box (which does not exist there). No predicate here:
    // the template only renders this for `design` when style images exist (ADO #8909).
    if (this.cardMode() === 'Stacked') {
      return {};
    }

    const designsButton = this.overlayButtons().find(o => o.nativeElement.name === 'design');
    const { x, y, height, width } = designsButton?.nativeElement.getBoundingClientRect();
    const viewStyleImagesButton = this.viewStyleImagesButton();

    const style: any = {};

    if (viewStyleImagesButton && control.coordinates && this.cardMode() !== 'None') {
      Object.assign(style, { left: `${Math.floor(x + width / 2 - viewStyleImagesButton._elementRef.nativeElement.getBoundingClientRect().width / 2)}px`, top: `${+designsButton?.nativeElement.style.getPropertyValue('top').replace(/[A-Za-z]/g, '') + height}px`, position: 'absolute' });
    }

    if (Object.keys(style).length < 1) {
      Object.assign(style, { visibility: 'hidden', 'pointer-events': 'none' });
    }

    return style;
  }

  viewStyleImages() {
    this.viewStyleImagesClicked.emit();
  }

  getRemoveButtonStyle(control: OverlayControl) {
    // Flows under its card when stacked, for the same reason as getStyleImageViewButtonStyle. The
    // predicate matters here: the template renders this wrapper for gemstones regardless, so without
    // it a Remove button would appear with nothing to remove (ADO #8909).
    if (this.cardMode() === 'Stacked') {
      return this.hasRemoveButton(control) ? {} : { display: 'none' } as any;
    }

    const cardButton = this.overlayButtons().find(o => o.nativeElement.name === control.key);
    const {x, y, height, width} = cardButton?.nativeElement.getBoundingClientRect();
    const removeButton = this.removeButtons().find(b => b.nativeElement.name === `${control.key}-button`);

    const style: any = {};

    if (removeButton && (control.key === 'gemstones' && this.selectedAttributes().gemstoneArrangement || control.key === 'engraving' && this.selectedAttributes().engravingText && this.selectedAttributes().engravingType) && control.coordinates && this.cardMode() !== 'None') {
      Object.assign(style, { left: `${Math.floor(x + width / 2 - removeButton.nativeElement.getBoundingClientRect().width / 2)}px`, top: `${+cardButton?.nativeElement.style.getPropertyValue('top').replace(/[A-Za-z]/g, '') + height}px`, position: 'absolute' });
    }

    if (Object.keys(style).length < 1) {
      Object.assign(style, { visibility: 'hidden', 'pointer-events': 'none' });
    }

    return style;
  }

  async getCurrentRingSnapshot(dataType?: string, position?: THREE.Vector3, finalDim?: number, shadowLiftFraction = 0) {
    const snapShotCamera = this.camera?.clone(true);
    const snapshotScene = this.scene?.clone(true);
    assert(snapShotCamera, 'Camera does not exist');
    assert(snapshotScene, 'Scene does not exist');

    snapShotCamera.aspect = 1;

    const originalModelGroup = snapshotScene.children.find(c => c.name === 'model group');
    assert(originalModelGroup, 'Model does not exist');

    snapshotScene.remove(originalModelGroup);
    const modelGroup = originalModelGroup.clone(true);
    modelGroup.clone(true);

    snapshotScene.add(modelGroup);

    if (this.selectedAttributeService.configuredItem().engravingText && this.selectedAttributeService.configuredItem().engravingType) {
      const itemAssetsToUse = await this.itemAssetsService.get(this.selectedAttributeService.configuredItem());
      const modelMeshes: any = modelGroup.children[0].children[0].children;
      const indexMap = this.getTextureLocationIndexMap(modelMeshes.length);
      const index = indexMap.findIndex(i => i === 'Inside');

      modelMeshes[index].material = (modelMeshes[index] as any).material.clone();

      const insideItemAssets = itemAssetsToUse.filter(itemAsset => itemAsset.location === 'Inside') ?? [];
      const textureKeysInItemAssets = insideItemAssets.flatMap(asset => [ItemAssetLocationToThreeJSKeyMap[asset.type]].flat());
      const texturesKeysToRemove = Object.keys(modelMeshes[index].material).filter((key: any) => !textureKeysInItemAssets.includes(key) && modelMeshes[index].material[key] instanceof THREE.Texture);
      texturesKeysToRemove.forEach(key => {
        modelMeshes[index].material[key] = null;
      });
      await this.addTextures(modelMeshes, insideItemAssets);
    }

    if (!this.snapshotRenderer) {
      this.snapshotRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    }

    const snapshotSize = finalDim ?? 422;
    this.snapshotRenderer.setSize(snapshotSize * snapShotCamera.aspect, snapshotSize);
    this.snapshotRenderer.setClearColor(0xffffff, 0);
    this.snapshotRenderer.toneMapping = THREE.NeutralToneMapping;
    this.snapshotRenderer.toneMappingExposure = 6;
    this.snapshotRenderer.outputColorSpace = THREE.SRGBColorSpace;

    if (position) {
      snapShotCamera.position.copy(position);
    } else {
      snapShotCamera.position.set(-8, -23, 40);
    }
    snapShotCamera.lookAt(0, 0, 0);
    snapShotCamera.fov = 40;
    snapShotCamera.updateProjectionMatrix();

    const shadowsGroup = modelGroup.children.find(c => c.name === 'shadows');
    const blurPlane = shadowsGroup?.children.find(c => c.name === 'Blur Plane');
    const shadowCam = shadowsGroup?.children.find(c => c.name === 'Shadow Camera');

    shadowsGroup?.removeFromParent();
    blurPlane?.removeFromParent();
    shadowCam?.removeFromParent();

    const renderTarget = new THREE.WebGLRenderTarget(50, 50);
    renderTarget.texture.generateMipmaps = false;

    const blurRenderTarget = new THREE.WebGLRenderTarget(50, 50);
    blurRenderTarget.texture.generateMipmaps = false;

    assert(blurPlane, 'No blur plane');
    assert(shadowCam, 'No shadow camera');

    const boundingBoxes: THREE.Box3[] = [];
    (modelGroup.children[0] as any).traverse((child: any) => {
      if (child.isMesh) {
        const boundingBox = new THREE.Box3();
        boundingBox.setFromObject(child);
        boundingBoxes.push(boundingBox);
      }
    });

    const sizes = boundingBoxes.map((boundingBox) => {
      const size = new THREE.Vector3()
      boundingBox.getSize(size);
      return size;
    });

    const dim = sizes.reduce((max, size) => {
      return Math.max(max, size.x, size.y, size.z);
    }, 0);

    const originalModelSizeVector = new THREE.Vector3(dim, dim, dim);
    const modelSizeVector = new THREE.Vector3(originalModelSizeVector.x, originalModelSizeVector.y, originalModelSizeVector.z);
    modelSizeVector.multiplyScalar(2);
    modelSizeVector.x *= 1.2;


    this.addShadows(boundingBoxes, originalModelSizeVector, modelGroup as any, blurPlane as any, shadowCam as any, renderTarget, shadowLiftFraction * dim);

    snapshotScene.overrideMaterial = this.depthMaterial ?? null;

    const initialClearAlpha = this.snapshotRenderer.getClearAlpha();
    this.snapshotRenderer.setClearAlpha(0);

    this.snapshotRenderer.setRenderTarget(renderTarget ?? null);


    this.snapshotRenderer.render(snapshotScene, shadowCam as THREE.Camera);

    snapshotScene.overrideMaterial = null;

    const horizontalBlurMaterial = new THREE.ShaderMaterial(HorizontalBlurShader);
    horizontalBlurMaterial.depthTest = false;

    const verticalBlurMaterial = new THREE.ShaderMaterial(VerticalBlurShader);
    verticalBlurMaterial.depthTest = false;

    const blurFactor = 2;
    this.blurShadow(blurFactor * 4, this.snapshotRenderer, blurPlane as any, shadowCam as any, renderTarget, blurRenderTarget, horizontalBlurMaterial, verticalBlurMaterial);
    this.blurShadow(blurFactor * 8.5, this.snapshotRenderer, blurPlane as any, shadowCam as any, renderTarget, blurRenderTarget, horizontalBlurMaterial, verticalBlurMaterial);

    this.snapshotRenderer.setRenderTarget(null);
    this.snapshotRenderer.setClearAlpha(initialClearAlpha);

    this.snapshotRenderer.render(snapshotScene, snapShotCamera);

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = finalDim ?? 455;
    finalCanvas.height = finalDim ?? 422;
    const finalContext = finalCanvas.getContext('2d');
    assert(finalContext, 'Context was null');
    finalContext.fillStyle = 'white';
    finalContext.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
    finalContext.drawImage(this.snapshotRenderer.domElement, finalCanvas.width / 2 - this.snapshotRenderer.domElement.width / 2, 15);

    return finalCanvas.toDataURL(dataType);
  }

  async getPresetProductImages(): Promise<string[]> {
    const positions = [
      new THREE.Vector3(-7.833406040660527, -23.38891039262857, 52.348507069016804),
      new THREE.Vector3(44.0976982023353, -23.72862123811839, 29.00198474761407),
      new THREE.Vector3(2.358326096472448e-15, -16.57905409450369, 55.442804197106504),
      new THREE.Vector3(0.00005786855425795648, 7.105427357601002e-15, 57.86855425793719),
      new THREE.Vector3(-45.45288030494169, 34.40831602431766, 9.943491955168362),
      new THREE.Vector3(-44.2939988731814, -35.38863192479173, 11.5955149183374),
      new THREE.Vector3(1.0038593959236344e-16, -57.868554257966125, 3.852555607863906e-9),
    ];
    const results: string[] = [];
    for (const position of positions) {
      results.push(await this.getCurrentRingSnapshot('image/jpeg', position, 2048));
    }
    return results;
  }

  // Turntable frames: orbit the camera a full 360° around the Z axis (the axis
  // OrbitControls' azimuthal angle rotates about, because camera.up is (0, 0, 1)),
  // starting from a fixed camera position and rotating that vector about Z. The
  // angle is negated so the ring turns the same direction the customizer spins.
  // Each frame reuses the existing single-frame snapshot pipeline (shadows too).
  // Lifts the shadow plane toward the ring for the turntable, as a fraction of
  // model size, to close the ring/shadow gap that the equatorial camera angle
  // exposes. Live-tunable from the console (window.mainImageComponent...) so the
  // value can be dialled in without a rebuild. Only affects the turntable path.
  turntableShadowLiftFraction = 0.1;

  async getTurntableImages(frameCount = 144, size = 512): Promise<string[]> {
    // Starting camera position — on the XY plane at the preset orbit radius.
    const start = new THREE.Vector3(28.934277128885583, 50.11563806773309, 3.852534291581833e-9);
    const results: string[] = [];
    for (let i = 0; i < frameCount; i++) {
      const angle = -(i / frameCount) * Math.PI * 2; // negative = reversed direction
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const position = new THREE.Vector3(
        start.x * cos - start.y * sin,
        start.x * sin + start.y * cos,
        start.z
      );
      results.push(await this.getCurrentRingSnapshot('image/png', position, size, this.turntableShadowLiftFraction));
    }
    return results;
  }

  removeGemstones() {
    this.selectedAttributeService.removeGemstones();
    const designView = this.navigationService.views().findIndex(v => v.title === 'Design');
    this.navigationService.navigateTo(designView);
  }
}
